/**
 * <site-search> — the site's product finder (snippets/site-search.liquid).
 *
 * Searchanise stays the engine (its index, synonyms, merchandising); this element owns the
 * experience on top of its API: intent → refinement → discovery → product.
 * - Query understanding: sizes ("200x300" → the nearest catalogue size), rooms, styles,
 *   colours, shapes, price caps and finishes typed into the field become refinements —
 *   API restrictions on real facets/tags, never guesses — and the rest stays a text query.
 * - Refinement chips come from the reply's facets (sizes, colours, shapes, with counts) and
 *   from the catalogue's tags (rooms, styles, light/dark, easy-clean), narrowing step by step.
 * - Zero query: recent searches, popular searches, rooms, sizes (the ones this shopper uses
 *   first), recently viewed and trending products. Zero results: a way out, never a dead end.
 * - Native <dialog>; keyboard: arrows walk the options, Enter picks, Escape closes.
 * - Requests: debounced, aborted when stale, cached; previous results stay while loading.
 * - Search memory: product click → back restores the query, refinements and scroll.
 * - Analytics: every step is a `site-search` DOM event + dataLayer push, and mapped onto the
 *   CJA SDK's event contract (search, search_results, search_result_click, filter_apply…).
 * Page mode (data-mode="page", sections/search-page.liquid): the same element inline on
 *   /search — an open non-modal dialog; the URL (q + rb_* params) is the state, results
 *   auto-load on scroll, the header icon focuses the field, nothing closes.
 * Everything from the API is set as text, never as HTML.
 */
if (!customElements.get('site-search')) {
  const DEBOUNCE_MS = 180;
  const MIN_CHARS = 2;
  const CACHE_SIZE = 20; // slim replies (cards + facets), not the raw API payloads
  const RUG_TYPE = 'שטיח'; // Searchanise product_type of the rugs (refinement-only searches are rugs)
  // A catalogue number ("31501090", "31501090-80150"): customer service looks products up by
  // SKU, so such a query also returns sold-out products (marked "אזל מהמלאי").
  const SKU_PATTERN = /^\s*[A-Za-z]{0,3}\d{5,}(?:[-_ ]?\d{2,})*\s*$/;
  const isSku = (value) => SKU_PATTERN.test(String(value || ''));
  // "31501090-200290", "31501090 200*290", "31501090200290": the same variant SKU.
  const skuKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9\u05d0-\u05ea]/g, '');
  // The engine indexes variant SKUs as "31501090-200290": a catalogue number typed with a
  // space or the size as 200*290 is sent in that form, so it finds the product.
  const skuForm = (text) => String(text || '')
    .replace(/^\s*([A-Za-z]{0,3}\d{5,})[\s\-_]+(\d{2,3})\s*[*x×X]\s*(\d{2,3})\s*$/, '$1-$2$3')
    .replace(/^\s*([A-Za-z]{0,3}\d{5,})\s+(\d{4,})\s*$/, '$1-$2');
  const RECENT_MAX = 6;
  const VIEWED_MAX = 8;
  const QUICK_MAX = 3; // refinement chips on the first phone screen (+ "עוד", one row at 375px)
  const MEMORY_TTL = 30 * 60 * 1000;
  const ENGINE_TIMEOUT = 9000; // ms before an engine request is retried
  const ENGINE_RETRIES = 1;
  const ENGINE_RETRY_WAIT = 500; // ms
  const FETCH_PAGE = 16; // fetched per request (re-ranked), PAGE_SHOWN drawn at a time
  const PAGE_SHOWN = 8;
  const LOW_RESULTS = 3; // this many results or fewer (with refinements) also offer the recovery choices
  const SORT_MIN = 24; // results page: sorting is offered above this many results
  const BROAD_MIN = 150; // a search this wide gets the room/style shortcut cards
  const MODEL_MAX = 80; // a model-name search returns at most this many
  const DAY_MS = 24 * 60 * 60 * 1000;
  const FEW_RESULTS = 3; // at or below: offer a spelling correction
  const LOW_STOCK = 2; // units left (whole product) for "נשארו מעט"
  const KEYS = {
    recent: 'site-search:recent',
    viewed: 'site-search:viewed',
    sizes: 'site-search:sizes',
    memory: 'site-search:memory',
    rooms: 'site-search:rooms',
    vocab: 'site-search:vocab',
    brands: 'site-search:brands',
    best: 'site-search:best',
    families: 'site-search:families',
    literal: 'site-search:literal',
  };
  /**
   * Feature flags for the search capabilities. Defaults here; the shop metafield custom.search_flags
   * (json, e.g. {"recovery": false}) overrides them without a deploy, and ?ss_flags=-recovery,+x
   * overrides them on one page view for QA. Read once per page (readFlags).
   */
  const FLAGS = {
    fuzzy: true, // one-letter typo correction of known words ("סלוו" → סלון)
    wishes: true, // notes for wishes no filter can promise ("לא משיר שערות")
    recovery: true, // counted one-change choices on zero / very few results
    dialogModel: true, // a model-name search shows its collection card in the dialog too
    localCollections: true, // collections matched from the store's own list, instantly
    learnedRank: false, // reorder within the page by what shoppers chose for the same search (analytics rank map)
    similar: false, // "שטיחים דומים": products like a given one (analytics similarity API)
    visualUpload: false, // search by a photo the shopper takes or picks (analytics visual API)
  };
  const readFlags = (raw) => {
    try {
      const stored = (raw ? JSON.parse(raw) : null) || {};
      Object.keys(FLAGS).forEach((key) => { if (typeof stored[key] === 'boolean') FLAGS[key] = stored[key]; });
    } catch (error) {
      // a broken metafield keeps the defaults
    }
    // "+" arrives as a space in a query string: "+x", " x" and "x" all turn x on
    (new URLSearchParams(window.location.search).get('ss_flags') || '').split(',').map((entry) => entry.trim()).forEach((entry) => {
      const key = entry.replace(/^[+-]/, '');
      if (key in FLAGS) FLAGS[key] = !entry.startsWith('-');
    });
  };
  const SIZE_OPTION = /מיד|size/i;
  // Hebrew typed with the keyboard on English ("xhsbh" = "סידני"): the standard Israeli layout,
  // key for key. q (/) and w (׳) are left out of words: a word holding them is not flipped.
  const LAYOUT_HE = { e: 'ק', r: 'ר', t: 'א', y: 'ט', u: 'ו', i: 'ן', o: 'ם', p: 'פ', a: 'ש', s: 'ד', d: 'ג', f: 'כ', g: 'ע', h: 'י', j: 'ח', k: 'ל', l: 'ך', ';': 'ף', z: 'ז', x: 'ס', c: 'ב', v: 'ה', b: 'נ', n: 'מ', m: 'צ', ',': 'ת', '.': 'ץ' };
  const LAYOUT_WORD = /^(?=.*[a-z])[a-z;,.]{3,}$/i; // three keys or more: "xh" mid-typing stays as typed
  const LAYOUT_FOCUSED = 60; // a Hebrew reading with at most this many products is a real match
  // Words that only frame the request ("שטיח … בצבע …"): dropped from the engine's text once something was understood.
  const GENERIC = /^(שטיח|שטיחים|שטיחי|שטיחון|של|עם|ל|בצבע|צבע|בגוון|גוון|rug|rugs|carpet|carpets)$/i;
  const STAR = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><path d="M8 1.5l1.9 4.1 4.4.5-3.3 3 .9 4.4L8 11.3l-3.9 2.2.9-4.4-3.3-3 4.4-.5z" fill="currentColor"/></svg>';
  const CHECK = '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const SEARCH_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>';
  const CLOCK_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>';
  const ARROW_ICON = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 6l-6 6 6 6"/></svg>';

  /* ------------------------------------------------------------------ catalogue knowledge
     Real values from Searchanise's index (facet buckets and tags), ordered by how many
     products carry them, so ties go to the size/room the store has most of. */
  const SIZES = [
    '160*230 - L', '200*290 - XL', '140*190 - M', '240*340 - XXL', '120*170 - S', '80*150 - XS',
    '300*400 - XXXL', '120*120 - M', '160*160 - L', '80*200 - M', '80*300 - L', '200*200 - XL',
    '200*300 - XL', '120*180 - S', '150*200 - M', '60*90 - XXS', '100*150 - XS', '170*240 - L',
  ].map((value, rank) => {
    const [w, h] = value.split(' ')[0].split('*').map(Number);
    return { value, w, h, rank };
  });
  const HOME_SIZES = ['80*150 - XS', '120*170 - S', '140*190 - M', '160*230 - L', '200*290 - XL', '240*340 - XXL', '300*400 - XXXL', '120*120 - M'];
  const ROOMS = [
    { label: 'סלון', tag: 'Rooms_שטיחים לסלון', words: ['סלון', 'סלונים', 'living', 'living room', 'livingroom', 'salon'] },
    { label: 'חדר שינה', tag: 'Rooms_שטיחים לחדר שינה', words: ['חדר שינה', 'שינה', 'חדר הורים', 'bedroom'] },
    { label: 'חדר ילדים', tag: 'Rooms_שטיחים לחדר ילדים', words: ['חדר ילדים', 'ילדים', 'ילד', 'ילדה', 'תינוק', 'תינוקות', 'חדר תינוק', 'kids', 'nursery'] },
    { label: 'פינת אוכל', tag: 'Rooms_שטיחים לפינת אוכל', words: ['פינת אוכל', 'אוכל', 'dining'] },
    { label: 'מסדרון', tag: 'Rooms_שטיחים למסדרון', words: ['מסדרון', 'פרוזדור', 'hallway', 'corridor'] },
    { label: 'כניסה', tag: 'Rooms_שטיחים לחלל כניסה', words: ['כניסה'] },
    { label: 'חדר עבודה', tag: 'Rooms_שטיחים לחדר עבודה', words: ['חדר עבודה', 'עבודה', 'משרד'] },
  ];
  const STYLES = [
    { label: 'מודרני', tag: 'Features_מודרני', words: ['מודרני', 'מודרנים', 'מודרניים', 'מודרנית', 'עכשווי', 'modern', 'contemporary'] },
    { label: 'קלאסי', tag: 'Features_קלאסי', words: ['קלאסי', 'קלאסים', 'קלאסיים', 'קלאסית', 'classic'] },
    { label: 'סקנדינבי', tag: 'Features_סקנדינבי', words: ['סקנדינבי', 'סקנדינבים', 'סקנדינביים', 'נורדי', 'נורדים', 'נורדיים'] },
    { label: 'בוהו', tag: 'Features_בוהושיק', words: ['בוהו', 'בוהושיק'] },
    { label: 'וינטג׳', tag: "Features_וינטג'", words: ['וינטג', "וינטג'", 'וינטג׳', 'וינטאג'] },
    { label: 'גיאומטרי', tag: 'Features_גיאומטרי', words: ['גיאומטרי', 'גאומטרי', 'גיאומטריים'] },
    { label: 'פרסי', tag: 'Features_פרסי', words: ['פרסי', 'פרסים', 'פרסיים'] },
    { label: 'אתני', tag: 'Features_אתני', words: ['אתני', 'אתניים'] },
    { label: 'כפרי', tag: 'Features_כפרי', words: ['כפרי', 'כפריים'] },
    { label: 'מרוקאי', tag: 'Features_מרוקאי', words: ['מרוקאי', 'מרוקאים', 'מרוקאיים'] },
    { label: 'אבסטרקט', tag: 'Features_אבסטרקט', words: ['אבסטרקט', 'מופשט', 'מופשטים'] },
  ];
  const FEATURES = [
    { label: 'בהיר', tag: 'Color Group_צבעים בהירים', words: ['בהיר', 'בהירים', 'בהירה', 'בהירות'] },
    { label: 'כהה', tag: 'Color Group_צבעים כהים', words: ['כהה', 'כהים', 'כהות'] },
    { label: 'קל לניקוי', tag: 'Features_קל במיוחד לניקוי', words: ['קל לניקוי', 'קל לנקות', 'שקל לנקות', 'קלים לניקוי', 'נוח לניקוי', 'קל לנקיון', 'רחיץ', 'רחיצים', 'רחיצה', 'לניקוי', 'ניקוי', 'כביסה', 'washable', 'easy clean'] },
    { label: 'צמר', tag: 'Features_צמר', words: ['צמר'] },
    { label: 'לבעלי חיים', tag: 'Features_מתאים לבעלי חיים', words: ['בעלי חיים', 'לבעלי חיים', 'כלבים', 'כלב', 'חתולים', 'חתול'] },
    { label: 'טבעי', tag: 'Features_עשוי מחומרים טבעיים', words: ['טבעי', 'טבעיים', 'טבעית', 'חומרים טבעיים'] },
    { label: 'לחוץ', tag: 'Features_שטיחי חוץ', words: ['חוץ', 'לחוץ', 'מרפסת', 'למרפסת', 'גינה', 'לגינה', 'חצר', 'outdoor'] },
    { label: 'דו צדדי', tag: 'Features_דו צדדי', words: ['דו צדדי', 'דו-צדדי', 'דו צדדיים', 'הפיך'] },
    { label: 'לקיץ', tag: 'Features_שטיח לקיץ', words: ['קיץ', 'לקיץ', 'קיצי', 'קיציים'] },
    { label: 'ממוחזר', tag: 'Features_עשוי מחומרים ממוחזרים', words: ['ממוחזר', 'ממוחזרים', 'ממוחזרת', 'מיחזור'] },
  ];
  /**
   * Intents: words that describe what a shopper wants rather than a property a rug carries
   * ("יוקרתי"). As plain text they match marketing copy in descriptions; here they become a
   * removable pill that filters to ANY of the intent's tags (OR within the group, AND with the
   * other refinements), from `minPrice` up, best sellers first. `link`: the curated collection,
   * offered above the results.
   * "יוקרתי" (2026-10-14): tags alone also caught ₪136–262 rugs and price-first showed only
   * ₪20k+ handmade pieces; tags + from ₪1,000 + best sellers gives Sun, Cleo, Zara, Baluch…
   */
  const INTENTS = [
    {
      id: 'premium',
      label: 'יוקרתי',
      phrase: 'יוקרתיים',
      words: ['יוקרתי', 'יוקרתית', 'יוקרתיים', 'יוקרתיות', 'יוקרה', 'יוקרת', 'פרימיום', 'מפואר', 'מפוארת', 'מפוארים', 'אקסקלוסיבי', 'אקסקלוסיביים', 'premium', 'luxury'],
      // the luxury Art line, one-of-a-kind handmade rugs, and the premium yarns
      tags: ['שטיחי יוקרה ART', 'OneOfAKind', 'Material_100% צמר', 'Material_פוליאסטר משולב ויסקוזה', 'Material_היטסט משולב ויסקוזה', 'Material_שניל', 'Material_ויסקוזה'],
      minPrice: 1000,
      link: { href: '/collections/prestige-art-rugs', label: 'לקולקציית שטיחי יוקרה Art' },
    },
    {
      // "שטיח זול" matched no product text at all (0 results). Cheapest-first put ₪47 door mats on
      // top (a product's price is its smallest size); best sellers from ₪400 down gives Sydney,
      // Bell, Sitar, Garda — the affordable rugs people actually buy.
      id: 'value',
      label: 'משתלם',
      phrase: 'משתלמים',
      words: ['זול', 'זולים', 'זולה', 'זולות', 'בזול', 'משתלם', 'משתלמים', 'משתלמת', 'מוזל', 'מוזלים', 'זול במיוחד', 'cheap'],
      tags: [],
      maxPrice: 400,
    },
    {
      id: 'soft',
      label: 'רך',
      phrase: 'רכים',
      words: ['רך', 'רכה', 'רכים', 'רכות', 'נעים', 'נעימה', 'נעימים', 'נעים למגע', 'פרוותי', 'פרוותיים', 'קטיפתי', 'קטיפתיים', 'פלאפי', 'soft'],
      tags: ['Features_נעים למגע', 'Features_שאגי'],
    },
  ];
  const intentOf = (id) => INTENTS.find((intent) => intent.id === id) || null;
  const COLOURS = ['קרם', 'אפור', "בז'", 'אדום', 'כחול', 'חום', 'לבן', 'שחור', 'ורוד', 'ירוק', 'סגול', 'צהוב', 'תכלת', 'צבעוני'];
  /** Other words for the catalogue's colours (facet values above) — never a colour the catalogue lacks. */
  const COLOUR_WORDS = {
    קרם: ['שמנת', 'לבן שבור', 'אופוויט', 'אוף וויט', 'off white', 'cream', 'ivory'],
    "בז'": ['בז', 'בייז', "בייז'", 'בזים', 'beige', 'נוד', 'חול'],
    אפור: ['אפורים', 'אפורה', 'אפרפר', 'גרי', 'grey', 'gray', 'פחם', 'אנתרציט'],
    אדום: ['אדומים', 'אדומה', 'בורדו', 'red'],
    כחול: ['כחולים', 'כחולה', 'נייבי', 'navy', 'blue'],
    חום: ['חומים', 'חומה', 'brown', 'שוקולד'],
    לבן: ['לבנים', 'לבנה', 'white'],
    שחור: ['שחורים', 'שחורה', 'black'],
    ורוד: ['ורודים', 'ורודה', 'פודרה', 'pink'],
    ירוק: ['ירוקים', 'ירוקה', 'זית', 'green'],
    סגול: ['סגולים', 'סגולה', 'purple'],
    צהוב: ['צהובים', 'צהובה', 'חרדל', 'yellow'],
    תכלת: ['טורקיז', 'light blue'],
    צבעוני: ['צבעוניים', 'צבעונית', 'צבעים', 'רב צבעוני', 'colorful', 'colourful'],
  };
  const colourOf = (candidates) => COLOURS.find((c) => candidates.includes(c) || candidates.includes(c.replace("'", '')) || (COLOUR_WORDS[c] || []).some((w) => candidates.includes(w))) || null;
  /**
   * Size classes ("גדול", "קטן"): several catalogue sizes as one filter (OR within the size facet).
   * An explicit size ("200x290") always wins over a class.
   */
  const SIZE_CLASSES = [
    { id: 'large', label: 'גדול', words: ['גדול', 'גדולים', 'גדולה', 'גדולות', 'ענק', 'ענקי', 'ענקיים', 'large', 'big'], values: ['200*290 - XL', '200*300 - XL', '240*340 - XXL', '300*400 - XXXL'] },
    { id: 'small', label: 'קטן', words: ['קטן', 'קטנים', 'קטנה', 'קטנות', 'small'], values: ['60*90 - XXS', '80*150 - XS', '100*150 - XS', '120*170 - S', '120*180 - S'] },
  ];
  const sizeClassOf = (id) => SIZE_CLASSES.find((c) => c.id === id) || null;
  /** Constraints the shopper set on purpose: relaxed only when they choose to (recovery panel). */
  const HARD_KINDS = new Set(['price', 'priceMin', 'size', 'sizeClass']);
  /**
   * Wishes the catalogue holds no data for: taken out of the engine's words (they only matched
   * marketing copy, or nothing) and answered honestly — never turned into a filter that would
   * claim a property the products are not tagged with.
   */
  const UNSUPPORTED = [
    { id: 'shedding', words: ['שלא משאיר שערות', 'לא משאיר שערות', 'שלא נושר', 'לא נושר', 'שלא משיר', 'לא משיר', 'בלי נשירה', 'ללא נשירה', 'לא מנסל'], note: 'אין לנו נתון מדויק על נשירת סיבים, ולכן לא סיננו לפיו. שטיחים קלים לניקוי הם בדרך כלל הבחירה הנכונה.', suggest: { kind: 'feature', value: 'Features_קל במיוחד לניקוי', label: 'קל לניקוי' } },
  ];
  /** Hebrew number words, for sizes in metres ("שני מטר על שלוש") and prices ("עד אלף"). */
  const NUMBER_WORDS = { אחד: 1, אחת: 1, מטר: 1, שני: 2, שניים: 2, שתיים: 2, שתי: 2, שלוש: 3, שלושה: 3, ארבע: 4, ארבעה: 4, חמש: 5, חמישה: 5 };
  const SHAPES = [
    { label: 'עגול', value: 'עגול', words: ['עגול', 'עגולים', 'עגולה', 'עגולות'] },
    { label: 'ראנר', value: 'ראנר', words: ['ראנר', 'ראנרים', 'רנר'] },
    { label: 'מלבן', value: 'מלבן', words: ['מלבן', 'מלבני', 'מלבניים'] },
  ];
  const PRICES = [500, 1000, 2000];
  /** Colour families that read as close to each other — the "צבעים קרובים" way out. */
  const NEAR_COLOURS = {
    קרם: ["בז'", 'לבן', 'אפור'], "בז'": ['קרם', 'חום'], אפור: ['שחור', "בז'", 'כחול'], שחור: ['אפור', 'חום'],
    לבן: ['קרם', 'אפור'], כחול: ['תכלת', 'אפור', 'ירוק'], תכלת: ['כחול', 'ירוק'], ירוק: ['כחול', 'צהוב'],
    אדום: ['ורוד', 'חום', 'צבעוני'], ורוד: ['אדום', 'סגול'], סגול: ['ורוד', 'כחול'], חום: ["בז'", 'אדום'],
    צהוב: ['ירוק', 'צבעוני'], צבעוני: ['אדום', 'כחול'],
  };
  /** Words that name nothing on their own (not vocabulary, not corrected). */
  const STOP_HE = new Set(['שטיחי', 'שטיחים', 'שטיח', 'שטיחון', 'קולקציית', 'קולקציה', 'לסלון', 'לחדר', 'שינה', 'ילדים', 'כל', 'של', 'עם', 'עד', 'סמ', 'במבצע', 'במלאי', 'חדשים', 'חדש', 'לבית', 'בגוון', 'מידה', 'מידות', 'צבע']);
  const STOP_EN = new Set(['rugs', 'rug', 'collection', 'in', 'stock', 'new', 'sale', 'outlet', 'all', 'the', 'and', 'for', 'of', 'with', 'cm', 'round', 'runner', 'room', 'living', 'bedroom', 'kids', 'handmade', 'super', 'rec', 'only', 'online', 'top']);
  const HEBREW = /^[֐-׿'"׳״-]+$/;
  const LATIN = /^[A-Za-z][A-Za-z'-]*$/;

  /** Damerau-Levenshtein (optimal string alignment), capped: stops early past `max`. */
  const editDistance = (a, b, max = 2) => {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;
    const rows = [];
    for (let i = 0; i <= a.length; i += 1) rows.push([i]);
    for (let j = 1; j <= b.length; j += 1) rows[0][j] = j;
    for (let i = 1; i <= a.length; i += 1) {
      let rowMin = Infinity;
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let value = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) value = Math.min(value, rows[i - 2][j - 2] + 1);
        rows[i][j] = value;
        if (value < rowMin) rowMin = value;
      }
      if (rowMin > max) return max + 1;
    }
    return rows[a.length][b.length];
  };
  /** A query as a rule key: trimmed, lower-case, one space, no trailing punctuation. */
  const normalise = (text) => String(text || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[?!.,;:]+$/, '');

  const stripFinal = (word) => word.replace(/[ךםןףץ]$/, (c) => ({ ך: 'כ', ם: 'מ', ן: 'נ', ף: 'פ', ץ: 'צ' })[c]);
  const SIZE_RE = /(\d{2,3})\s*(?:x|X|×|\*|על|-|\/)\s*(\d{2,3})/;
  const CURRENCY = '(?:₪|שח|ש"ח|שקל(?:ים)?|nis|ils)?';
  const PRICE_RE = new RegExp(`(?:עד|מתחת ל-?|פחות מ-?|לא יותר מ-?|מקסימום|תקציב(?: של)?(?: עד)?)\\s*₪?\\s*(\\d{2,6})\\s*${CURRENCY}`);
  const PRICE_MIN_RE = new RegExp(`(?:מעל|יותר מ-?|החל מ-?|מינימום|לפחות)\\s*₪?\\s*(\\d{2,6})\\s*${CURRENCY}`);
  /** "1,500 ש״ח" with no "עד": a price the shopper has in mind, read as the cap. Needs a currency word. */
  const PRICE_BARE_RE = new RegExp(`(?:ב-?)?(\\d{3,6})\\s*(?:₪|שח|ש"ח|שקל(?:ים)?|nis|ils)(?=\\s|$)`);
  const PRICE_RANGE_RE = new RegExp(`בין\\s*₪?\\s*(\\d{2,6})\\s*${CURRENCY}\\s*(?:ל-?|לבין|ועד|עד|-)\\s*₪?\\s*(\\d{2,6})\\s*${CURRENCY}`);
  /**
   * The typed text with sizes and prices in one form, before parsing: "2 על 3", "2.4x3.4",
   * "שני מטר על שלוש" → "200x300"; "1,000" → "1000"; "עד אלף" → "עד 1000"; ׳ ״ → ' ".
   */
  const normaliseQuery = (typed) => {
    let text = String(typed || '').replace(/׳/g, "'").replace(/״/g, '"').replace(/(\d),(\d{3})\b/g, '$1$2');
    text = text.replace(/\bאלפיים\b/g, '2000').replace(/(^|\s)אלף(?=\s|$)/g, '$11000');
    const num = '(\\d(?:[.,]\\d{1,2})?|' + Object.keys(NUMBER_WORDS).join('|') + ')';
    const unit = '(?:\\s*(?:מטר(?:ים)?|מ\'|מ))?';
    const metres = new RegExp(`(^|\\s)${num}${unit}(\\s*וחצי)?\\s*(?:x|X|×|\\*|על|\\/)\\s*${num}${unit}(\\s*וחצי)?(?=\\s|$)`);
    text = text.replace(metres, (whole, lead, a, halfA, b, halfB) => {
      const value = (v, half) => (NUMBER_WORDS[v] ?? parseFloat(String(v).replace(',', '.'))) + (half ? 0.5 : 0);
      const w = value(a, halfA);
      const h = value(b, halfB);
      if (!(w > 0 && w <= 5 && h > 0 && h <= 6)) return whole;
      return `${lead}${Math.round(w * 100)}x${Math.round(h * 100)}`;
    });
    return text;
  };

  /* ------------------------------------------------------------------ helpers */
  const money = (value) => `₪${Math.round(parseFloat(value)).toLocaleString('en-US')}`;
  const fill = (label = '', values = {}) => label.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');
  /** "160×230" as one left-to-right run (isolated, so RTL text never flips the numbers). */
  const ltr = (text) => `\u2066${text}\u2069`;
  const sizeLabel = (value) => (value === 'OS' ? 'מידה אחת' : ltr(value.split(' ')[0].replace('*', '×')));
  const sizeFamily = (value) => (value.includes(' - ') ? value.split(' - ')[1] : '');
  /** The title without the English collection name (snippets/product-display-title.liquid). */
  const displayTitle = (title) => {
    const words = title.trim().split(/\s+/).filter((word) => word !== '&' && !(word.length >= 3 && word === word.toUpperCase() && word !== word.toLowerCase()));
    while (words.length && ['-', '–', '·'].includes(words[words.length - 1])) words.pop();
    return words.length ? words.join(' ') : title.trim();
  };
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  const icon = (svg) => {
    const span = el('span', 'ss__ic');
    span.innerHTML = svg; // fixed icons, no data in them
    return span;
  };
  const storage = {
    read(key, session = false) {
      try {
        const value = (session ? sessionStorage : localStorage).getItem(key);
        return value ? JSON.parse(value) : null;
      } catch (error) {
        return null;
      }
    },
    write(key, value, session = false) {
      try {
        (session ? sessionStorage : localStorage).setItem(key, JSON.stringify(value));
      } catch (error) {
        // private mode / storage blocked: conveniences only
      }
    },
    remove(key, session = false) {
      try {
        (session ? sessionStorage : localStorage).removeItem(key);
      } catch (error) {
        // ignore
      }
    },
  };
  const sameRefinement = (a, b) => a.kind === b.kind && a.value === b.value;
  /** A stem's last letter in its final form (אדומ → אדום), so plurals match the catalogue's words. */
  const finalLetter = (word) => word.replace(/[מנצפכ]$/, (c) => ({ מ: 'ם', נ: 'ן', צ: 'ץ', פ: 'ף', כ: 'ך' })[c]);

  /** The nearest catalogue sizes to a typed width × height (either orientation). */
  const nearestSizes = (a, b) => {
    const fits = (typed, real) => Math.abs(typed - real) <= Math.max(15, real * 0.12);
    const matches = SIZES.filter(({ w, h }) => (fits(a, w) && fits(b, h)) || (fits(b, w) && fits(a, h)));
    const exact = matches.find(({ w, h }) => (w === a && h === b) || (w === b && h === a));
    // The exact bucket wins only when the store really stocks it in depth; otherwise the
    // popular neighbour (e.g. 200×300 → 200×290 with hundreds of rugs, not the 15 exact ones).
    const best = exact && exact.rank < 8 ? exact : matches[0];
    return best ? [best, ...matches.filter((size) => size !== best)] : [];
  };

  /* ------------------------------------------------------------------ query understanding
     Turns the typed text into refinements (real facet values / tags) plus the residual text
     query. Words are matched whole, with a Hebrew prefix (ל/ב/ה/ו/מ) or plural stripped. */
  const parseQuery = (typed, brands = null) => {
    let text = typed.trim();
    const detected = [];
    let nearest = null;
    // A catalogue number ("31501090-200290", "31501090 200*290") is a lookup, never words to
    // understand: its digits would read as a size ("090-200") or a price.
    if (isSku(text) || isSku(skuForm(text))) return { detected, text, nearest, notes: [] };
    // English brand names become the Hebrew word the titles carry ("bell" → "בל").
    if (brands && brands.size) {
      text = text.split(/\s+/).map((token) => (LATIN.test(token) && brands.get(token.toLowerCase())) || token).join(' ');
    }
    text = normaliseQuery(text);
    const notes = [];
    if (FLAGS.wishes) UNSUPPORTED.forEach((wish) => {
      const hit = wish.words.find((w) => text.includes(w));
      if (hit) {
        notes.push(wish);
        text = text.replace(hit, ' ');
      }
    });
    const size = text.match(SIZE_RE);
    if (size) {
      const sizes = nearestSizes(parseInt(size[1], 10), parseInt(size[2], 10));
      if (sizes.length) {
        detected.push({ kind: 'size', value: sizes[0].value, label: sizeLabel(sizes[0].value) });
        nearest = { typed: ltr(`${size[1]}×${size[2]}`), alternatives: sizes.slice(1, 3), exact: sizes[0].w === parseInt(size[1], 10) && sizes[0].h === parseInt(size[2], 10) };
      }
      text = text.replace(size[0], ' ');
    }
    const range = text.match(PRICE_RANGE_RE);
    if (range) {
      const [low, high] = [parseInt(range[1], 10), parseInt(range[2], 10)].sort((a, b) => a - b);
      detected.push({ kind: 'priceMin', value: String(low), label: `מ-₪${low.toLocaleString('en-US')}` });
      detected.push({ kind: 'price', value: String(high), label: `עד ₪${high.toLocaleString('en-US')}` });
      text = text.replace(range[0], ' ');
    }
    const price = text.match(PRICE_RE);
    if (price) {
      detected.push({ kind: 'price', value: price[1], label: `עד ₪${parseInt(price[1], 10).toLocaleString('en-US')}` });
      text = text.replace(price[0], ' ');
    }
    const bare = !price && !range ? text.match(PRICE_BARE_RE) : null;
    if (bare) {
      detected.push({ kind: 'price', value: bare[1], label: `עד ₪${parseInt(bare[1], 10).toLocaleString('en-US')}` });
      text = text.replace(bare[0], ' ');
    }
    const floor = text.match(PRICE_MIN_RE);
    if (floor) {
      detected.push({ kind: 'priceMin', value: floor[1], label: `מ-₪${parseInt(floor[1], 10).toLocaleString('en-US')}` });
      text = text.replace(floor[0], ' ');
    }
    const tokens = text.split(/\s+/).filter(Boolean);
    const used = new Set();
    const forms = (token) => {
      const list = [token];
      if (/^[ולבהמ]/.test(token) && token.length > 3) list.push(token.slice(1));
      list.slice().forEach((form) => {
        if (form.endsWith('ים') || form.endsWith('ות')) list.push(finalLetter(form.slice(0, -2)));
        if (form.endsWith('ה') || form.endsWith('ת')) list.push(finalLetter(form.slice(0, -1)));
      });
      return list;
    };
    const lookup = (phrase) => {
      const candidates = forms(phrase);
      const hit = (entries, kind, pick) => {
        for (const entry of entries) {
          if (entry.words.some((word) => candidates.includes(word))) return pick(entry, kind);
        }
        return null;
      };
      return (
        hit(ROOMS, 'room', (r) => ({ kind: 'room', value: r.tag, label: r.label })) ||
        hit(SHAPES, 'shape', (s) => ({ kind: 'shape', value: s.value, label: s.label })) ||
        hit(STYLES, 'style', (s) => ({ kind: 'style', value: s.tag, label: s.label })) ||
        hit(FEATURES, 'feature', (f) => ({ kind: 'feature', value: f.tag, label: f.label })) ||
        hit(INTENTS, 'intent', (x) => ({ kind: 'intent', value: x.id, label: x.label })) ||
        hit(SIZE_CLASSES, 'sizeClass', (x) => ({ kind: 'sizeClass', value: x.id, label: x.label })) ||
        (() => {
          const colour = colourOf(candidates);
          return colour ? { kind: 'color', value: colour, label: colour } : null;
        })()
      );
    };
    /** One slip of the finger in a dictionary word ("סלוו", "מודרנו"): words of 4+ letters, edit distance 1. */
    const DICTIONARY = [
      ...[ROOMS, STYLES, FEATURES, INTENTS, SIZE_CLASSES].flatMap((list) => list.flatMap((entry) => entry.words)),
      ...COLOURS, ...Object.values(COLOUR_WORDS).flat(),
    ].filter((word) => word.length >= 4 && !word.includes(' ') && HEBREW.test(word));
    const fuzzy = (token) => {
      if (!FLAGS.fuzzy || token.length < 4 || !HEBREW.test(token) || GENERIC.test(token)) return null;
      if (LITERAL.has(stripFinal(token))) return null; // a real name in the catalogue ("ניקול"), not a typo of "ניקוי"
      const near = DICTIONARY.find((word) => editDistance(token, word, 1) <= 1);
      const hit = near ? lookup(near) : null;
      return hit ? { ...hit, fuzzy: token } : null;
    };
    for (let i = 0; i < tokens.length; i += 1) {
      if (used.has(i)) continue;
      if (i + 1 < tokens.length) {
        const pair = lookup(`${tokens[i]} ${tokens[i + 1]}`);
        if (pair) {
          detected.push(pair);
          used.add(i).add(i + 1);
          continue;
        }
      }
      const one = lookup(tokens[i]) || fuzzy(tokens[i]);
      if (one) {
        detected.push(one);
        used.add(i);
      }
    }
    // An exact size wins over a size class ("שטיח גדול 200x290" is the one size).
    if (detected.some((r) => r.kind === 'size')) {
      for (let k = detected.length - 1; k >= 0; k -= 1) if (detected[k].kind === 'sizeClass') detected.splice(k, 1);
    }
    const residual = tokens.filter((token, i) => !used.has(i) && !(detected.length && GENERIC.test(token)));
    return { detected, text: detected.length ? residual.join(' ') : text.trim(), nearest, notes };
  };

  let flagsRead = false;
  /**
   * Words the catalogue itself uses (product / collection names): never "corrected" by the one-letter
   * typo match — "ניקול" is a rug, not a misspelt "ניקוי". Filled from the collections vocabulary, the
   * families seen in results, and every word a search proved real (search() checks a correction's
   * word against product titles before applying it); kept on the device (KEYS.literal).
   */
  const LITERAL = new Set();

  /* Learned ranking (FLAGS.learnedRank). The analytics service aggregates, per search key, which
     products shoppers clicked / carted / bought against how often each was shown at each position
     (position-bias corrected, smoothed); it returns small bounded boosts. The storefront only
     applies them, with its own guards: within the fetched page, never under an explicit sort, never
     past the exact-SKU pin, at most RANK_MAX_MOVE places, and a holdout that keeps the engine order. */
  const RANK_MAX_MOVE = 4;
  const RANK_HOLDOUT = 0.1; // share of sessions that keep the engine order (the comparison)
  const RANK_WAIT_MS = 150; // the map may arrive this long after the results; later → next search
  const RANK_TTL_MS = 30 * 60 * 1000;
  const VISUAL_MAX_SIDE = 640; // a photo is scaled down on the device before it is sent
  /** The analytics service's origin: the one the SDK script loads from (layout/theme.liquid #cja-sdk). */
  const analyticsOrigin = () => {
    try {
      const sdk = document.querySelector('script[src*="/api/analytics/sdk.js"]');
      return sdk ? new URL(sdk.src).origin : '';
    } catch (error) {
      return '';
    }
  };
  const withTimeout = (signal, ms) => (AbortSignal.any && AbortSignal.timeout ? AbortSignal.any([signal, AbortSignal.timeout(ms)]) : signal);
  /** One search's key for the rank map: its words (sorted, no generic words) and its refinements. */
  const rankKey = (text, refinements) => {
    const words = (text || '').toLowerCase().split(/\s+/).filter((w) => w && !GENERIC.test(w)).map(stripFinal).sort();
    const refs = refinements.map((r) => `${r.kind}:${r.value}`).sort();
    return [words.join(' '), ...refs].join('|');
  };

  class SiteSearch extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-ss-form]');
      this.input = this.querySelector('[data-ss-input]');
      this.clearButton = this.querySelector('[data-ss-clear]');
      this.spinner = this.querySelector('[data-ss-spinner]');
      this.body = this.querySelector('[data-ss-body]');
      this.rail = this.querySelector('[data-ss-rail]');
      this.main = this.querySelector('[data-ss-main]');
      this.status = this.querySelector('[data-ss-status]');
      this.foot = this.querySelector('[data-ss-foot]');
      this.allLink = this.querySelector('[data-ss-all]');
      this.sheet = this.querySelector('[data-ss-sheet]');
      this.sheetBody = this.querySelector('[data-ss-sheet-body]');
      this.sheetApply = this.querySelector('[data-ss-sheet-apply]');
      if (!this.dialog || !this.input) return;

      const json = (selector) => {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent || '{}');
        } catch (error) {
          return {};
        }
      };
      this.labels = json('[data-ss-labels]');
      this.data = json('[data-ss-data]');
      this.shortcuts = json('[data-ss-shortcuts]'); // results page: rooms + styles cards (snippets/search-shortcuts.liquid)
      // Facet attribute ids by refinement kind (theme settings); a missing id disables that kind.
      this.attrs = { size: this.dataset.sizeAttr || '', color: this.dataset.colorAttr || '', shape: this.dataset.shapeAttr || '' };
      this.cache = new Map();
      this.sequence = 0;
      this.picked = []; // refinements chosen with chips
      this.reply = null; // the reply on screen
      this.shown = []; // cards on screen (for positions and "load more")
      this.optionSeq = 0;
      this.desktop = window.matchMedia('(min-width: 768px)');
      this.page = this.dataset.mode === 'page';
      // Menu mode: the same search inline in the mobile menu's search panel (snippets/mobile-menu.liquid).
      // Phone layout only, no history entry, no URL, no restore; "back" returns to the menu.
      this.menu = this.dataset.mode === 'menu';
      this.sort = ''; // page mode: '', 'sales', 'price-asc', 'price-desc', 'new'
      this.toolbar = this.querySelector('[data-ss-toolbar]');
      this.toolbarChips = this.querySelector('[data-ss-toolbar-chips]');
      this.progress = this.querySelector('[data-ss-progress]');

      if (Array.isArray(this.data.viewed) && this.data.viewed[0]) this.rememberViewed(this.data.viewed[0]);
      this.buffer = []; // fetched but not yet drawn (FETCH_PAGE > PAGE_SHOWN)
      this.fetched = 0; // items fetched for the current view (the next startIndex)
      this.brands = new Map(); // english → hebrew brand word, learned from titles + collections
      if (!flagsRead) {
        readFlags(this.dataset.flags);
        flagsRead = true;
      }
      this.vocab = []; // known Hebrew names for spelling correction
      this.collections = []; // the store's rug collections [{ title, handle, count }], for instant matches
      this.best = new Set(); // bestseller product ids (daily)
      this.families = new Set(); // rug family names seen in results ("בוסטון")
      this.loadKnowledge();

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      document.addEventListener(
        'click',
        (event) => {
          // "שטיחים דומים" on a product (snippets/search-similar.liquid): the dialog shows products like it.
          const similar = FLAGS.similar && !this.page && !this.menu && event.target.closest('[data-search-similar]');
          if (similar) {
            event.preventDefault();
            this.opener = similar;
            this.openSimilar({ id: similar.dataset.searchSimilar, title: similar.dataset.searchSimilarTitle || '' });
            return;
          }
          const opener = event.target.closest('[data-search-open]');
          if (!opener || this.menu) return; // the header's search opens the dialog instance only
          event.preventDefault();
          if (this.page) {
            // On the results page the field is already on screen: go to it.
            this.input.focus({ preventScroll: true });
            this.input.scrollIntoView({ block: 'center', behavior: 'smooth' });
            return;
          }
          this.opener = opener;
          this.open('icon');
        },
        { signal }
      );
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.input.addEventListener('input', () => this.onInput(), { signal });
      this.addEventListener('change', (event) => {
        if (event.target.matches?.('[data-ss-visual-file]')) this.onVisualFile(event.target);
      }, { signal });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      // The quick-add span sits inside a card link: Enter/Space open it, not the link.
      this.addEventListener('keydown', (event) => {
        const quick = (event.key === 'Enter' || event.key === ' ') && event.target.closest?.('[data-ss-quick]');
        if (!quick) return;
        event.preventDefault();
        event.stopPropagation();
        this.openQuick(quick);
      }, { signal });
      // Phones: a swipe through the results lowers the keyboard (the usual app pattern).
      this.body.addEventListener('touchmove', () => this.dismissKeyboard(), { signal, passive: true });
      this.sheet?.addEventListener('click', (event) => { if (event.target === this.sheet) this.closeSheet(); }, { signal });
      this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      this.dialog.addEventListener('close', () => this.onClose(), { signal });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close('escape');
      }, { signal });
      this.dialog.addEventListener('pointerdown', (event) => {
        if (event.target === this.dialog) this.close('backdrop');
      }, { signal });
      this.desktop.addEventListener('change', () => this.layout(), { signal });
      window.addEventListener('pageshow', (event) => this.onPageShow(event), { signal });
      // Leaving for the results page (also into the bfcache) and coming back to the tab.
      window.addEventListener('pagehide', () => this.settleAllLink(), { signal });
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.settleAllLink(); }, { signal });
      // The open dialog is a history entry, so Back closes it (phones) instead of leaving the page.
      window.addEventListener('popstate', () => {
        if (this.page || this.menu || !this.dialog.open) return;
        this.viaHistory = true;
        this.close('back');
      }, { signal });
      if (this.page) this.initPage();
      else if (!this.menu) this.onPageShow({ persisted: false });
    }

    /** Page mode: the URL is the query — q and rb_<attribute> refinements. */
    initPage() {
      const params = new URLSearchParams(window.location.search);
      this.input.value = (params.get('q') || '').trim();
      this.clearButton.hidden = !this.input.value;
      this.picked = [];
      const kinds = Object.fromEntries(Object.entries(this.attrs).filter(([, attr]) => attr).map(([kind, attr]) => [attr, kind]));
      this.sort = ['sales', 'price-asc', 'price-desc', 'new'].includes(params.get('sort')) ? params.get('sort') : '';
      (params.get('intent') || '').split(',').map(intentOf).filter(Boolean).forEach((intent) => {
        this.picked.push({ kind: 'intent', value: intent.id, label: intent.label });
      });
      params.forEach((value, name) => {
        if (!name.startsWith('rb_')) return;
        const attribute = name.slice(3);
        if (attribute === 'price') {
          // "min|max": only the cap is the shopper's; a floor (or the value intent's cap) comes from an intent
          let cap = value.split('|')[1] || '';
          const intentCaps = (params.get('intent') || '').split(',').map(intentOf).filter((x) => x && x.maxPrice).map((x) => String(x.maxPrice));
          if (intentCaps.includes(cap)) cap = '';
          if (cap) this.picked.push({ kind: 'price', value: cap, label: fill(this.labels.priceUpTo, { price: parseInt(cap, 10).toLocaleString('en-US') }) });
          // a floor that is not an intent's ("יוקרתי" brings its own) is the shopper's "מעל ₪X"
          const low = value.split('|')[0] || '';
          const intentFloors = INTENTS.filter((x) => x.minPrice && (params.get('rb_tags') || '').includes(x.tags.join('|'))).map((x) => String(x.minPrice));
          if (low && !intentFloors.includes(low)) this.picked.push({ kind: 'priceMin', value: low, label: `מ-₪${parseInt(low, 10).toLocaleString('en-US')}` });
        } else if (attribute === 'tags') {
          value.split(',').filter(Boolean).forEach((tag) => {
            const intent = INTENTS.find((x) => x.tags.join('|') === tag);
            if (intent) {
              this.picked.push({ kind: 'intent', value: intent.id, label: intent.label });
              return;
            }
            const known = [...ROOMS.map((r) => ({ kind: 'room', tag: r.tag, label: r.label })), ...STYLES.map((r) => ({ kind: 'style', tag: r.tag, label: r.label })), ...FEATURES.map((r) => ({ kind: 'feature', tag: r.tag, label: r.label }))].find((r) => r.tag === tag);
            this.picked.push(known ? { kind: known.kind, value: tag, label: known.label } : { kind: 'feature', value: tag, label: tag.split('_').pop() });
          });
        } else if (kinds[attribute]) {
          const kind = kinds[attribute];
          const sizeClass = kind === 'size' && SIZE_CLASSES.find((c) => c.values.join('|') === value);
          if (sizeClass) this.picked.push({ kind: 'sizeClass', value: sizeClass.id, label: sizeClass.label });
          else this.picked.push({ kind, value, label: kind === 'size' ? sizeLabel(value) : value });
        }
      });
      this.openedAt = Date.now();
      this.track('search_open', { source: 'page' });
      this.searchSource = 'page';
      if (this.query || this.picked.length) this.search();
      else this.renderHome();
      // Results keep coming as the shopper scrolls (a few pages, then the button).
      this.autoPages = 0;
      this.moreObserver = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting) || this.autoPages >= 3) return;
        this.autoPages += 1;
        this.loadMore();
      }, { rootMargin: '600px 0px' });
      this.observeMore();
    }

    observeMore() {
      if (!this.moreObserver) return;
      this.moreObserver.disconnect();
      const more = this.main.querySelector('.ss__more');
      if (more && !more.hidden) this.moreObserver.observe(more);
    }

    disconnectedCallback() {
      this.moreObserver?.disconnect();
      this.abortController?.abort();
      this.request?.abort();
      this.settleAllLink();
      clearTimeout(this.timer);
      clearTimeout(this.skeletonTimer);
      clearTimeout(this.resetTimer);
      clearTimeout(this.enterTimer);
    }

    /* ---------------------------------------------------------------- open / close */

    open(source = 'icon') {
      if (this.page) return; // always open: the page is the search
      if (this.menu) {
        // Called inside the tap that showed the menu's search panel, so phones raise the keyboard.
        this.openedAt = Date.now();
        this.typedOnce = false;
        this.loadKnowledge();
        if (!this.query && !this.picked.length) this.renderHome();
        this.input.focus({ preventScroll: true });
        this.track('search_open', { source });
        return;
      }
      if (this.dialog.open) return;
      clearTimeout(this.resetTimer);
      if (this.resetTimer) {
        // Reopened mid-slide-out: reset now so the last search does not come back.
        this.resetTimer = null;
        if (source !== 'restore') {
          this.input.value = '';
          this.picked = [];
          this.reply = null;
          this.clearButton.hidden = true;
        }
      }
      if (!this.pushed) {
        try {
          history.pushState({ ...(history.state || {}), ssOpen: true }, '');
          this.pushed = true;
        } catch (error) {
          this.pushed = false;
        }
      }
      this.openedAt = Date.now();
      this.typedOnce = false;
      this.dialog.showModal();
      // The blocks rise in turn while the panel slides in — only on open, not on every update.
      this.dialog.classList.add('is-entering');
      clearTimeout(this.enterTimer);
      this.enterTimer = setTimeout(() => this.dialog.classList.remove('is-entering'), 700);
      document.documentElement.classList.add('ss-open');
      this.watchViewport(true);
      if (!this.query && !this.picked.length) this.renderHome();
      this.input.focus({ preventScroll: true });
      this.track('search_open', { source });
    }

    close(reason = 'button') {
      this.settleAllLink();
      if (this.page) return; // nothing to close on the results page
      if (this.menu) {
        this.dismissKeyboard();
        this.request?.abort();
        this.track('search_close', { reason, hadQuery: !!this.query, refinements: this.activeRefinements().length });
        this.dispatchEvent(new CustomEvent('site-search:back', { bubbles: true }));
        return;
      }
      if (!this.dialog.open) return;
      this.closeReason = reason;
      if (this.pushed && !this.viaHistory) {
        if (this.closing) return; // Back already requested: a second one would leave the page
        this.closing = true;
        history.back(); // popstate closes the dialog
        return;
      }
      this.viaHistory = false;
      this.closing = false;
      this.dialog.close();
    }

    onClose() {
      document.documentElement.classList.remove('ss-open');
      this.watchViewport(false);
      this.request?.abort();
      clearTimeout(this.timer);
      this.track('search_close', { reason: this.closeReason || 'button', hadQuery: !!this.query, refinements: this.activeRefinements().length });
      this.closeReason = null;
      this.pushed = false;
      this.closing = false;
      this.setBusy(false);
      storage.remove(KEYS.memory, true);
      if (this.opener?.isConnected && this.opener.offsetParent) this.opener.focus({ preventScroll: true });
      this.opener = null;
      // The panel is still sliding out: reset its content once it is off screen.
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        if (this.dialog.open) return;
        this.input.value = '';
        this.picked = [];
        this.reply = null;
        this.clearButton.hidden = true;
        this.renderHome();
        this.resetTimer = null;
      }, 360);
    }

    /* ---------------------------------------------------------------- events */

    onClick(event) {
      const target = event.target;
      const quick = target.closest('[data-ss-quick]');
      if (quick) {
        event.preventDefault();
        event.stopPropagation();
        this.openQuick(quick);
        return;
      }
      if (target.closest('[data-ss-close]')) return this.close('button');
      if (target.closest('[data-ss-retry]')) {
        this.track('search_retry', {});
        this.cache.clear();
        this.search();
        return;
      }
      if (target.closest('[data-ss-clear-all]')) {
        this.picked = [];
        this.input.value = '';
        this.clearButton.hidden = true;
        this.track('search_clear', { all: true });
        this.searchSource = 'clear';
        this.search();
        return;
      }
      if (target.closest('[data-ss-clear]')) {
        this.track('search_clear', {});
        this.input.value = '';
        this.picked = [];
        this.input.focus();
        this.onInput(true);
        return;
      }
      if (target.closest('[data-ss-visual]')) {
        this.querySelector('[data-ss-visual-file]')?.click();
        return;
      }
      const forget = target.closest('[data-ss-forget]');
      if (forget) {
        const query = forget.dataset.ssForget;
        const kept = query ? this.readRecent().filter((item) => item !== query) : [];
        storage.write(KEYS.recent, kept);
        this.renderHome();
        return;
      }
      if (target.closest('[data-ss-layout-model]')) this.track('search_correction_model_click', { from: this.correction?.from || this.query, to: this.correction?.to || null, kind: 'layout' });
      if (target.closest('[data-ss-redirect]')) this.track('search_redirect_click', { ruleId: target.closest('[data-ss-redirect]').dataset.ssRedirect });
      if (target.closest('[data-ss-intent-link]')) this.track('search_intent_link_click', { intent: target.closest('[data-ss-intent-link]').dataset.ssIntentLink, query: this.query });
      const suggestion = target.closest('[data-ss-suggest]');
      if (suggestion) {
        const { ssSuggest: text, ssSource: source } = suggestion.dataset;
        this.searchSource = source || 'suggestion';
        this.track(source === 'popular' || source === 'recent' ? 'search_query' : 'search_suggestion_click', { text, source, from: this.query });
        if (source === 'correction') this.track('search_correction_click', { from: this.correction?.from || this.query, to: text, kind: this.correction?.kind || 'spelling' });
        if (source === 'as-typed') {
          // Back to the words as typed, without the keyboard-layout reading this once.
          this.skipLayout = text;
          this.track('search_correction_reject', { from: text, to: this.correction?.to || null, kind: this.correction?.kind || 'layout' });
        }
        this.input.value = text;
        this.input.focus();
        this.onInput(true);
        return;
      }
      const recovery = target.closest('[data-ss-recovery]');
      if (recovery) {
        const option = (this.recovery || [])[Number(recovery.dataset.ssRecovery)];
        if (option) this.applyRecovery(option);
        return;
      }
      const sortChip = target.closest('[data-ss-sort-value]');
      if (sortChip) {
        const value = sortChip.dataset.ssSortValue;
        if (value === this.sort) return;
        this.sort = value;
        this.searchSource = 'sort';
        this.track('search_sort', { sort: this.sort || 'relevance' });
        this.search();
        return;
      }
      if (target.closest('[data-ss-clear-filters]')) {
        // Every refinement off, the words kept (detected ones are taken out of the field).
        let text = this.query;
        parseQuery(text).detected.forEach((refinement) => { text = this.stripRefinement(text, refinement); });
        this.picked = [];
        this.input.value = text;
        this.clearButton.hidden = !text;
        this.track('search_clear', { filters: true });
        this.searchSource = 'refine';
        this.search();
        return;
      }
      const shortcut = target.closest('[data-ss-shortcut]');
      if (shortcut) this.track('search_shortcut_click', { kind: shortcut.dataset.ssShortcut, label: shortcut.textContent.trim(), href: shortcut.getAttribute('href'), query: this.query });
      const modelLink = target.closest('[data-ss-model]');
      if (modelLink) this.track('search_model_click', { family: modelLink.dataset.ssModel, href: modelLink.getAttribute('href'), query: this.query });
      const refine = target.closest('[data-ss-refine]');
      if (refine) {
        this.dismissKeyboard();
        this.toggleRefinement({ kind: refine.dataset.ssKind, value: refine.dataset.ssRefine, label: refine.dataset.ssLabel }, refine.getAttribute('aria-pressed') === 'true');
        return;
      }
      const remove = target.closest('[data-ss-remove]');
      if (remove) {
        this.removeRefinement({ kind: remove.dataset.ssKind, value: remove.dataset.ssRemove });
        return;
      }
      if (target.closest('[data-ss-more-chips]')) {
        this.openSheet();
        return;
      }
      if (target.closest('[data-ss-sheet-close]')) {
        this.closeSheet();
        return;
      }
      if (target.closest('[data-ss-sheet-reset]')) {
        if (this.picked.length) {
          this.picked = [];
          this.searchSource = 'refine';
          this.search();
        }
        return;
      }
      if (target.closest('[data-ss-load-more]')) {
        this.dismissKeyboard();
        this.loadMore();
        return;
      }
      const category = target.closest('[data-ss-category]');
      if (category) this.track('search_category_click', { text: category.textContent.trim(), href: category.getAttribute('href'), source: category.dataset.ssCategory || 'engine' });
      const product = target.closest('[data-ss-product]');
      if (product) this.onProductClick(product);
      const all = target.closest('[data-ss-all]');
      if (all) {
        this.remember(this.query);
        this.track('search_view_all', { count: this.reply ? Number(this.reply.totalItems) || 0 : 0 });
        // A plain link (works without this script): only its look changes while the page loads.
        all.classList.add('is-loading');
        all.setAttribute('aria-busy', 'true');
        // Never stuck: the page is left for the results (pagehide / bfcache restore settle it
        // too), and a navigation that does not happen within a few seconds gives the link back.
        clearTimeout(this.allTimer);
        this.allTimer = setTimeout(() => this.settleAllLink(), 8000);
      }
    }

    onProductClick(card) {
      const position = parseInt(card.dataset.ssPosition, 10);
      const source = card.dataset.ssSource || 'results';
      if (this.query) this.remember(this.query);
      this.track('search_product_click', {
        productId: card.dataset.ssId,
        position: Number.isNaN(position) ? undefined : position + 1,
        source,
        resultCount: this.reply ? Number(this.reply.totalItems) || 0 : undefined,
        msFromOpen: this.openedAt ? Date.now() - this.openedAt : undefined,
        correctedFrom: this.correction?.from || null,
        correctedTo: this.correction?.to || null,
        correctionKind: this.correction?.kind || null,
        ...(source === 'results' ? this.rankContext() : {}),
      });
      // So Back brings the search back as it was (query, refinements, scroll).
      if (source === 'results' && !this.page) {
        storage.write(KEYS.memory, { typed: this.input.value, picked: this.picked, scrollTop: this.body.scrollTop, at: Date.now() }, true);
      }
    }

    /**
     * Phones: the keyboard covers the bottom of the screen (iOS does not resize the page for
     * it), so the panel follows the visual viewport: it ends right above the keyboard and
     * the "show all results" bar stays in sight while typing.
     */
    watchViewport(on) {
      const vv = window.visualViewport;
      if (!vv || this.page) return;
      if (!on) {
        this.viewportController?.abort();
        this.viewportController = null;
        this.dialog.style.removeProperty('--ss-vh');
        this.dialog.style.removeProperty('--ss-vtop');
        return;
      }
      if (this.viewportController) return;
      this.viewportController = new AbortController();
      const sync = () => {
        if (this.desktop.matches) {
          this.dialog.style.removeProperty('--ss-vh');
          this.dialog.style.removeProperty('--ss-vtop');
          return;
        }
        this.dialog.style.setProperty('--ss-vh', `${Math.round(vv.height)}px`);
        this.dialog.style.setProperty('--ss-vtop', `${Math.round(vv.offsetTop)}px`);
      };
      const { signal } = this.viewportController;
      vv.addEventListener('resize', sync, { signal });
      vv.addEventListener('scroll', sync, { signal });
      sync();
    }

    /** Quick add from a result card, in the page's <quick-view>. */
    openQuick(trigger) {
      const view = document.querySelector('quick-view');
      if (!view || typeof view.open !== 'function') {
        window.location.assign(trigger.dataset.ssQuick);
        return;
      }
      this.dismissKeyboard();
      this.track('search_quick_view', { href: trigger.dataset.ssQuick, position: Number(trigger.closest('[data-ss-product]')?.dataset.ssPosition) || 0 });
      view.open(trigger.dataset.ssQuick, trigger);
    }

    /** Phones: lower the keyboard so the results (and the filters) are visible. */
    dismissKeyboard() {
      if (document.activeElement === this.input && !this.desktop.matches) this.input.blur();
    }

    onSubmit(event) {
      event.preventDefault();
      const query = this.query;
      const active = this.activeOption();
      if (active) {
        active.click();
        return;
      }
      if (!query && !this.picked.length) return;
      this.remember(query);
      this.track('search_submit', { count: this.reply ? Number(this.reply.totalItems) || 0 : undefined });
      if (this.page) {
        this.searchSource = 'typed';
        this.search();
        return;
      }
      window.location.assign(this.resultsUrl());
    }

    onKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const options = [...this.body.querySelectorAll('[data-ss-option]')];
      if (!options.length) return;
      event.preventDefault();
      const current = options.indexOf(this.activeOption());
      const next = event.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
      this.setActiveOption(options[next]);
    }

    activeOption() {
      const id = this.input.getAttribute('aria-activedescendant');
      return id ? this.body.querySelector(`#${CSS.escape(id)}`) : null;
    }

    setActiveOption(option) {
      this.body.querySelectorAll('[data-ss-option].is-active').forEach((node) => {
        node.classList.remove('is-active');
        node.removeAttribute('aria-selected');
      });
      if (!option) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      option.classList.add('is-active');
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    /** The "all results" link back to a link (after a navigation, a restore from bfcache, a close). */
    settleAllLink() {
      clearTimeout(this.allTimer);
      this.allLink?.classList.remove('is-loading');
      this.allLink?.removeAttribute('aria-busy');
    }

    onPageShow(event) {
      this.settleAllLink();
      if (this.menu) return;
      if (event.persisted) return; // bfcache: the dialog is exactly as it was
      const memory = storage.read(KEYS.memory, true);
      const nav = performance.getEntriesByType?.('navigation')?.[0];
      const restore = memory && Date.now() - (memory.at || 0) <= MEMORY_TTL && nav?.type === 'back_forward';
      if (!restore) {
        // A stale "open" entry (reload with the search open): forget it, or Back would misbehave.
        if (history.state?.ssOpen) {
          try {
            const { ssOpen, ...rest } = history.state;
            history.replaceState(Object.keys(rest).length ? rest : null, '');
          } catch (error) {
            // ignore
          }
        }
        return;
      }
      storage.remove(KEYS.memory, true);
      this.picked = Array.isArray(memory.picked) ? memory.picked : [];
      this.input.value = memory.typed || '';
      this.restoreScroll = memory.scrollTop || 0;
      this.pushed = true; // the product was opened from the dialog, so this entry is ours
      this.opener = document.querySelector('[data-search-open]');
      this.open('restore');
      this.clearButton.hidden = !this.input.value;
      this.searchSource = 'restore';
      this.search();
    }

    /* ---------------------------------------------------------------- rules
       Mappings managed in the analytics dashboard, served with the CJA SDK's remote config
       (window.__CJA_REMOTE__.searchRules; cached ~5 min). Schema:
       { version, corrections: { "בולטון": "בוסטון" | { to, id } },
         synonyms: [{ from: ["שטיח כביס", "רחיץ"], to: "קל לניקוי", id }],
         redirects: { "הדמיה": { url: "/pages/roomvo", label: "הדמיה בבית", id } } }
       Read lazily (the SDK is deferred) and compiled once per version. Nothing here
       touches the network; without the SDK the search simply runs without rules. */

    rules() {
      const raw = window.__CJA_REMOTE__?.searchRules || window.__CJA_SEARCH_RULES__ || null;
      if (!raw || typeof raw !== 'object') return null;
      const version = String(raw.version ?? '0');
      if (this.compiledRules && this.compiledRules.version === version) return this.compiledRules;
      const corrections = new Map();
      Object.entries(raw.corrections || {}).forEach(([from, value]) => {
        const to = typeof value === 'string' ? value : value?.to;
        if (from && to) corrections.set(normalise(from), { to: String(to), id: (typeof value === 'object' && value.id) || `correction:${normalise(from)}` });
      });
      const synonyms = (Array.isArray(raw.synonyms) ? raw.synonyms : [])
        .filter((rule) => rule && rule.to && (Array.isArray(rule.from) ? rule.from.length : rule.from))
        .map((rule, index) => ({ from: (Array.isArray(rule.from) ? rule.from : [rule.from]).map(normalise).filter(Boolean), to: String(rule.to), id: rule.id || `synonym:${index}` }));
      const redirects = new Map();
      Object.entries(raw.redirects || {}).forEach(([from, value]) => {
        const url = typeof value === 'string' ? value : value?.url;
        if (from && url) redirects.set(normalise(from), { url: String(url), label: (typeof value === 'object' && value.label) || String(from), id: (typeof value === 'object' && value.id) || `redirect:${normalise(from)}` });
      });
      this.compiledRules = { version, corrections, synonyms, redirects };
      return this.compiledRules;
    }

    /** The typed text after the dashboard's rules: { text, ruleIds, redirect, corrected }. */
    applyRules(typed) {
      const result = { text: typed, ruleIds: [], redirect: null, corrected: null };
      const rules = this.rules();
      if (!rules) return result;
      const key = normalise(typed);
      const redirect = rules.redirects.get(key);
      if (redirect) {
        result.redirect = redirect;
        result.ruleIds.push(redirect.id);
      }
      const correction = rules.corrections.get(key);
      if (correction) {
        result.text = correction.to;
        result.corrected = { from: typed, to: correction.to };
        result.ruleIds.push(correction.id);
        return result;
      }
      let text = ` ${key} `;
      rules.synonyms.forEach((rule) => {
        rule.from.forEach((phrase) => {
          if (text.includes(` ${phrase} `)) {
            text = text.split(` ${phrase} `).join(` ${rule.to} `);
            if (!result.ruleIds.includes(rule.id)) result.ruleIds.push(rule.id);
          }
        });
      });
      if (result.ruleIds.length) result.text = text.trim();
      return result;
    }

    /* ---------------------------------------------------------------- state */

    get query() {
      return this.input.value.trim();
    }

    /** What the field means right now: detected refinements + the ones picked with chips. */
    parsed() {
      const parsed = parseQuery(this.applyRules(this.query).text, this.brands);
      const refinements = [...parsed.detected];
      this.picked.forEach((refinement) => {
        if (!refinements.some((other) => sameRefinement(other, refinement))) refinements.push(refinement);
      });
      return { ...parsed, refinements };
    }

    activeRefinements() {
      return this.parsed().refinements;
    }

    toggleRefinement(refinement, isOn) {
      if (isOn) return this.removeRefinement(refinement);
      // One size, shape, room or price at a time; styles/features/colours stack.
      const single = ['size', 'sizeClass', 'shape', 'room', 'price', 'priceMin', 'color'].includes(refinement.kind);
      this.picked = this.picked.filter((other) => !(single ? other.kind === refinement.kind : sameRefinement(other, refinement)));
      this.picked.push(refinement);
      this.searchSource = 'refine';
      if (this.zero && this.query) {
        // Picked from "nothing found": the typed words are what failed — start from the refinement.
        this.input.value = '';
        this.clearButton.hidden = true;
      }
      this.track('search_filter_click', { kind: refinement.kind, value: refinement.value, on: true });
      if (refinement.kind === 'size') this.rememberSize(refinement.value);
      this.search();
    }

    removeRefinement(refinement) {
      this.dropRefinement(refinement);
      this.track('search_filter_click', { kind: refinement.kind, value: refinement.value, on: false });
      this.searchSource = 'refine';
      this.search();
    }

    /** Take one refinement out — from the picked chips, or its words out of the field — without searching. */
    dropRefinement(refinement) {
      const before = this.picked.length;
      this.picked = this.picked.filter((other) => !sameRefinement(other, refinement));
      if (this.picked.length === before) {
        // It came from the typed text: take the words out of the field.
        const parsed = parseQuery(this.query);
        if (parsed.detected.some((other) => sameRefinement(other, refinement))) {
          this.input.value = this.stripRefinement(this.query, refinement);
          this.clearButton.hidden = !this.input.value;
        }
      }
    }

    /* ---------------------------------------------------------------- recovery
       Zero or very few results: one change at a time — a similar colour, a nearby size, one
       soft wish less, a higher budget — each counted on the real catalogue (a 1-item request,
       cached), ranked so the shopper's hard limits (price, size) are the last thing offered and
       never changed without a tap. */

    async recoveryOptions(parsed, signal, floor = 0) {
      const refs = parsed.refinements;
      const options = [];
      const replace = (from, to, label, kind, rank) => options.push({ remove: from ? [from] : [], add: to ? [to] : [], label, kind, rank });
      refs.forEach((r) => {
        if (r.kind === 'color') {
          (NEAR_COLOURS[r.value] || []).slice(0, 2).forEach((c) => replace(r, { kind: 'color', value: c, label: c }, fill(this.labels.recoverColour, { colour: c }), 'soft', 1));
        } else if (r.kind === 'size') {
          const current = SIZES.find((x) => x.value === r.value);
          if (current) {
            SIZES.filter((x) => x.value !== r.value).map((x) => ({ x, d: this.sizeDistance(current, x.value) })).sort((a, b) => a.d - b.d).slice(0, 2)
              .forEach(({ x }) => replace(r, { kind: 'size', value: x.value, label: sizeLabel(x.value) }, fill(this.labels.recoverSize, { size: sizeLabel(x.value) }), 'hard', 3));
          }
          replace(r, null, this.labels.recoverAnySize, 'hard', 4);
        } else if (r.kind === 'sizeClass') {
          replace(r, null, this.labels.recoverAnySize, 'hard', 4);
        } else if (r.kind === 'price') {
          const cap = Number(r.value);
          [1.25, 1.5, 2, 3].forEach((f, i) => {
            const next = Math.ceil((cap * f) / 50) * 50;
            replace(r, { kind: 'price', value: String(next), label: fill(this.labels.priceUpTo, { price: next.toLocaleString('en-US') }) }, fill(this.labels.recoverBudget, { price: next.toLocaleString('en-US') }), 'hard', 5 + i * 0.1);
          });
        } else if (r.kind === 'priceMin') {
          replace(r, null, this.labels.recoverNoFloor, 'hard', 5);
        } else {
          replace(r, null, fill(this.labels.noneWithout, { label: r.label }), 'soft', 2);
        }
      });
      if (!options.length) return [];
      const counted = await Promise.all(options.map(async (o) => {
        const next = refs.filter((r) => !o.remove.some((x) => sameRefinement(x, r))).concat(o.add);
        try {
          const reply = await this.fetchReply({ ...this.requestParams(parsed.text, next), maxResults: 1, facets: false, suggestions: false, categories: false }, signal);
          return { ...o, count: Number(reply.totalItems) || 0 };
        } catch (error) {
          return { ...o, count: 0 };
        }
      }));
      // the smallest budget step that finds something is enough; then softest change first, then most results
      const seenBudget = new Set();
      return counted
        .filter((o) => o.count > floor) // a choice must find more than the shopper already has
        .filter((o) => {
          const budget = o.add[0] && o.add[0].kind === 'price';
          if (!budget) return true;
          if (seenBudget.size) return false;
          seenBudget.add(o);
          return true;
        })
        .sort((a, b) => a.rank - b.rank || b.count - a.count)
        .slice(0, 5);
    }

    /** The recovery choices as buttons (count on each); `title` heads the group. */
    recoveryGroup(title, options) {
      const group = this.group(title, 'ss__group--recovery');
      const list = el('ul', 'ss__recovery');
      list.setAttribute('role', 'list');
      options.forEach((o, i) => {
        const li = el('li');
        const button = el('button', `ss__recovery-btn${o.kind === 'hard' ? ' is-hard' : ''}`);
        button.type = 'button';
        button.dataset.ssRecovery = String(i);
        button.append(el('span', 'ss__recovery-label', o.label), el('span', 'ss__recovery-count', o.count === 1 ? this.labels.intentOne : fill(this.labels.intentResults, { count: o.count.toLocaleString('en-US') })));
        li.append(button);
        list.append(li);
      });
      group.append(list);
      this.recovery = options;
      return group;
    }

    /** Fill `slot` with the counted choices for the view on screen (skipped if the shopper moved on). */
    async fillRecovery(slot, view, title) {
      const options = await this.recoveryOptions(view.parsed, this.request?.signal, Number(view.reply?.totalItems) || 0).catch(() => []);
      if (this.view !== view || !slot.isConnected) return;
      if (!options.length) {
        slot.remove();
        return;
      }
      slot.replaceChildren(this.recoveryGroup(title, options));
      this.track('search_recovery_shown', { query: this.query, total: Number(view.reply?.totalItems) || 0, options: options.map((o) => ({ label: o.label, count: o.count, kind: o.kind })) });
    }

    applyRecovery(option) {
      option.remove.forEach((r) => this.dropRefinement(r));
      option.add.forEach((r) => {
        const single = ['size', 'sizeClass', 'shape', 'room', 'price', 'priceMin', 'color'].includes(r.kind);
        this.picked = this.picked.filter((other) => !(single ? other.kind === r.kind : sameRefinement(other, r)));
        this.picked.push(r);
      });
      this.track('search_recovery_click', { query: this.query, label: option.label, count: option.count, kind: option.kind });
      this.searchSource = 'recovery';
      this.search();
    }

    /** The typed text without the words that produced one refinement. */
    stripRefinement(typed, refinement) {
      const tidy = (text) => text.replace(/\s+/g, ' ').trim();
      if (refinement.kind === 'size' || refinement.kind === 'price' || refinement.kind === 'priceMin') {
        const text = normaliseQuery(typed);
        if (refinement.kind === 'size') return tidy(text.replace(SIZE_RE, ' '));
        const range = text.match(PRICE_RANGE_RE);
        if (range) {
          // "בין 500 ל-1000": the other end stays
          const [low, high] = [parseInt(range[1], 10), parseInt(range[2], 10)].sort((a, b) => a - b);
          return tidy(text.replace(range[0], refinement.kind === 'price' ? ` מעל ${low} ` : ` עד ${high} `));
        }
        if (refinement.kind === 'price' && !PRICE_RE.test(text)) return tidy(text.replace(PRICE_BARE_RE, ' '));
        return tidy(text.replace(refinement.kind === 'price' ? PRICE_RE : PRICE_MIN_RE, ' '));
      }
      const tokens = typed.split(/\s+/);
      for (let n = 2; n >= 1; n -= 1) {
        for (let i = 0; i + n <= tokens.length; i += 1) {
          // a pair with a generic word ("שטיח אפור") is the one word that made the refinement:
          // removing "אפור" must leave "שטיח", not empty the search
          if (n === 2 && tokens.slice(i, i + n).some((token) => GENERIC.test(token))) continue;
          const phrase = tokens.slice(i, i + n).join(' ');
          const hit = parseQuery(phrase).detected[0];
          if (hit && sameRefinement(hit, refinement)) return [...tokens.slice(0, i), ...tokens.slice(i + n)].join(' ').trim();
        }
      }
      return typed;
    }

    /* ---------------------------------------------------------------- searching */

    onInput(immediate = false) {
      const query = this.query;
      this.clearButton.hidden = !this.input.value;
      this.setActiveOption(null);
      clearTimeout(this.timer);
      if (!this.typedOnce && query) {
        this.typedOnce = true;
        this.track('search_query_started', {});
      }
      if (query.length < MIN_CHARS && !this.picked.length) {
        this.request?.abort();
        this.sequence += 1; // any reply in flight is stale now
        this.setBusy(false);
        this.reply = null;
        this.renderHome();
        return;
      }
      this.timer = setTimeout(() => this.search(), immediate ? 0 : DEBOUNCE_MS);
    }

    requestParams(text, refinements, startIndex = 0) {
      const params = {
        api_key: this.dataset.key,
        q: skuForm(text),
        maxResults: FETCH_PAGE,
        startIndex,
        items: true,
        facets: true,
        categories: !!text,
        suggestions: !!text,
        pages: false,
        vendors: false,
        tags: false,
        categoriesMaxResults: 3,
        suggestionsMaxResults: 5,
        output: 'json',
        'restrictBy[quantity]': '1|', // in stock only
      };
      if (isSku(this.query) || isSku(text)) delete params['restrictBy[quantity]'];
      this.restrictions(refinements).forEach(([attribute, value]) => {
        params[`restrictBy[${attribute}]`] = value;
      });
      if (!text) {
        // Refinements only ("סלון", a size…): rugs, best sellers first — relevance means
        // nothing without words, and the store's pads/poufs would surface.
        params['restrictBy[product_type]'] = RUG_TYPE;
        params.sortBy = 'sales_amount';
        params.sortOrder = 'desc';
      }
      // an intent with its own order ("זול": the cheapest first) — unless the shopper chose a sort
      const ordered = refinements.map((r) => r.kind === 'intent' && intentOf(r.value)).find((intent) => intent && intent.sort);
      if (ordered) [params.sortBy, params.sortOrder] = ordered.sort;
      const sorts = { sales: ['sales_amount', 'desc'], 'price-asc': ['price', 'asc'], 'price-desc': ['price', 'desc'], new: ['created', 'desc'] };
      if (sorts[this.sort]) [params.sortBy, params.sortOrder] = sorts[this.sort];
      return params;
    }

    /**
     * Refinements as Searchanise restrictions: [attribute, value] pairs — the same for the
     * API (restrictBy[attribute]) and the results page (rb_attribute). Tags join with a
     * comma (AND); a facet kind whose attribute id is not configured is skipped.
     */
    restrictions(refinements) {
      const pairs = [];
      const tags = [];
      let priceMin = '';
      let priceMax = '';
      refinements.forEach(({ kind, value }) => {
        if (kind === 'price') priceMax = String(Math.min(Number(priceMax) || Infinity, Number(value)));
        else if (kind === 'priceMin') priceMin = String(Math.max(Number(priceMin) || 0, Number(value)));
        else if (kind === 'sizeClass') {
          // several sizes, one group (OR); an explicit size refinement replaces it (parseQuery)
          const sizeClass = sizeClassOf(value);
          if (sizeClass && this.attrs.size && !refinements.some((r) => r.kind === 'size')) pairs.push([this.attrs.size, sizeClass.values.join('|')]);
        } else if (kind === 'intent') {
          // one group: any of the intent's tags ("|" = OR inside a group, "," = AND between groups)
          const intent = intentOf(value);
          if (intent && intent.tags.length) tags.push(intent.tags.join('|'));
          if (intent && intent.minPrice) priceMin = String(Math.max(Number(priceMin) || 0, intent.minPrice));
          if (intent && intent.maxPrice) priceMax = String(Math.min(Number(priceMax) || Infinity, intent.maxPrice));
        } else if (kind in this.attrs) {
          if (this.attrs[kind]) pairs.push([this.attrs[kind], value]);
        } else tags.push(value);
      });
      if (priceMin || priceMax) pairs.push(['price', `${priceMin}|${priceMax}`]);
      if (tags.length) pairs.push(['tags', tags.join(',')]);
      return pairs;
    }

    /**
     * One API call, cached. The raw reply is ~400KB (every variant carries 250 currency
     * fields); only what the dialog draws is kept: card records, the three facets, the
     * suggestions and categories, the total.
     */
    /**
     * The engine's reply, with a time limit and one retry: a reply that takes longer than
     * ENGINE_TIMEOUT (the engine answers in well under a second when healthy) or a dropped
     * connection is tried once more before the search gives up. The outer signal (a newer
     * query) still cancels at once.
     */
    async fetchEngine(url, signal) {
      let lastError = null;
      for (let attempt = 0; attempt <= ENGINE_RETRIES; attempt += 1) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const inner = new AbortController();
        const onAbort = () => inner.abort();
        signal?.addEventListener('abort', onAbort, { once: true });
        const timer = setTimeout(() => inner.abort(new DOMException('Timeout', 'TimeoutError')), ENGINE_TIMEOUT);
        try {
          const response = await fetch(url, { signal: inner.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return await response.json();
        } catch (error) {
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
          lastError = error;
          if (attempt < ENGINE_RETRIES) await new Promise((resolve) => setTimeout(resolve, ENGINE_RETRY_WAIT));
        } finally {
          clearTimeout(timer);
          signal?.removeEventListener('abort', onAbort);
        }
      }
      throw lastError || new Error('search engine');
    }

    async fetchReply(params, signal) {
      const key = JSON.stringify(params);
      if (this.cache.has(key)) return this.cache.get(key);
      const url = new URL(this.dataset.api);
      Object.entries(params).forEach(([name, value]) => url.searchParams.set(name, value));
      const data = await this.fetchEngine(url, signal);
      const wanted = new Set(Object.values(this.attrs).filter(Boolean));
      const reply = {
        totalItems: Number(data.totalItems) || 0,
        items: (Array.isArray(data.items) ? data.items : []).map(SiteSearch.fromApi),
        suggestions: (Array.isArray(data.suggestions) ? data.suggestions : []).filter((text) => typeof text === 'string'),
        categories: (Array.isArray(data.categories) ? data.categories : []).filter((c) => c && c.title && c.link).map((c) => ({ title: c.title, link: c.link })),
        facets: (Array.isArray(data.facets) ? data.facets : [])
          .filter((f) => wanted.has(f.attribute))
          .map((f) => ({ attribute: f.attribute, buckets: (f.buckets || []).map((b) => ({ value: b.value, title: b.title, count: Number(b.count) || 0, color_code: b.color_code })) })),
      };
      this.cache.set(key, reply);
      if (this.cache.size > CACHE_SIZE) this.cache.delete(this.cache.keys().next().value);
      return reply;
    }

    async search() {
      const typed = this.query;
      let parsed = this.parsed();
      if (!typed && !parsed.refinements.length) {
        this.reply = null;
        this.renderHome();
        return;
      }
      const sequence = ++this.sequence;
      this.request?.abort();
      this.request = new AbortController();
      const { signal } = this.request;
      const rule = this.applyRules(typed);
      const source = this.searchSource || 'typed';
      this.searchSource = null;
      this.setBusy(true);
      this.rank = { key: rankKey(parsed.text, parsed.refinements), arm: this.rankArm(), map: null };
      const rankLoad = this.rank.arm === 'learned' ? this.loadRankMap(this.rank.key, signal) : null;
      // A one-letter correction ("ניקול" → קל לניקוי) is checked first: when the word as typed is in
      // product titles (sold-out ones too), it is a name — the search keeps it, and remembers it.
      const corrected = parsed.refinements.filter((r) => r.fuzzy).map((r) => r.fuzzy);
      if (corrected.length) {
        const params = { ...this.requestParams(corrected.join(' '), []), maxResults: 8, facets: false, suggestions: false, categories: false };
        delete params['restrictBy[quantity]'];
        const check = await this.fetchReply(params, signal).catch(() => null);
        if (sequence !== this.sequence) return;
        const real = corrected.filter((word) => check && SiteSearch.mentions(check.items, [word]));
        if (real.length) {
          this.rememberLiteral(real);
          parsed = this.parsed();
        }
      }
      try {
        let reply = await this.fetchReply(this.requestParams(parsed.text, parsed.refinements), signal);
        if (rankLoad) this.rank.map = await Promise.race([rankLoad, new Promise((resolve) => setTimeout(() => resolve(null), RANK_WAIT_MS))]);
        // What the shopper asked for before any rescue — logged as the zero it was.
        const original = reply.items.length ? null : { text: parsed.text, refinements: parsed.refinements.map((r) => `${r.kind}:${r.value}`) };
        // Wrong keyboard layout. The query as typed always goes first; its Hebrew reading
        // replaces it only when nothing found mentions the typed word (no results, or the
        // engine's loose matches) and the Hebrew reading finds products that do — so "boston"
        // or "kilim" never even cost a request. A Hebrew reading with a typo gets the spelling
        // help too ("xhsbg" → "סידנע" → "סידני"). "חיפוש כפי שהוקלד" turns it off once.
        let layout = null;
        // Stays off for the same words (adding a filter keeps it); new words reset it.
        const asTyped = !!this.skipLayout && this.skipLayout === parsed.text;
        const flip = asTyped ? null : this.layoutFlip(parsed.text);
        if (flip && !SiteSearch.mentions(reply.items, flip.from)) {
          const alt = await this.fetchReply(this.requestParams(flip.text, parsed.refinements), signal);
          const altTotal = alt.totalItems || alt.items.length;
          // Accepted when its titles name the Hebrew word, or (the original found nothing) when
          // it is a focused set: the engine's loose matches for gibberish run to the hundreds.
          if (altTotal && (SiteSearch.mentions(alt.items, flip.to) || (!reply.items.length && altTotal <= LAYOUT_FOCUSED))) {
            layout = { from: parsed.text, to: this.namedAs(alt.items, flip) };
            // The engine's suggestions repeat the flipped letters ("סידנ 80×150"): in the
            // catalogue's words, like the heading ("סידני 80×150").
            const flipped = flip.text.split(/\s+/);
            const named = layout.to.split(/\s+/);
            const suggestions = [...new Set(alt.suggestions.map((text) => text.split(/\s+/).map((w) => {
              const i = flipped.indexOf(w);
              return i >= 0 && named[i] ? named[i] : w;
            }).join(' ')))];
            reply = { ...alt, suggestions };
            parsed = { ...parsed, text: flip.text };
          } else if (!reply.items.length) {
            for (const fixed of this.candidates(flip.text).slice(0, 2)) {
              const better = await this.fetchReply(this.requestParams(fixed, parsed.refinements), signal);
              if (better.items.length) {
                layout = { from: parsed.text, to: fixed };
                reply = better;
                parsed = { ...parsed, text: fixed };
                break;
              }
            }
            if (!layout) {
              // Understood, but every match is sold out ("tnxyrso" = אמסטרדם): say so on the
              // zero page, with the model one tap away, instead of a bare "not found".
              const params = this.requestParams(flip.text, parsed.refinements);
              delete params['restrictBy[quantity]'];
              const any = await this.fetchReply(params, signal);
              const anyTotal = any.totalItems || any.items.length;
              if (anyTotal && (SiteSearch.mentions(any.items, flip.to) || anyTotal <= LAYOUT_FOCUSED)) {
                layout = { from: parsed.text, to: this.namedAs(any.items, flip), soldOut: true, url: any.items[0]?.link || '' };
              }
            }
          }
        }
        let fallback = false;
        // The understood query found nothing: try the words as typed before giving up — unless it
        // holds a hard constraint (price, size): those are never dropped silently; the recovery
        // panel offers them as explicit, counted choices instead.
        if (!reply.items.length && parsed.detected.length && !parsed.refinements.some((r) => HARD_KINDS.has(r.kind))) {
          const plain = await this.fetchReply(this.requestParams(typed, this.picked), signal);
          if (plain.items.length) {
            reply = plain;
            fallback = true;
          }
        }
        let dropped = '';
        if (!reply.items.length && parsed.text && parsed.refinements.length) {
          // Words + refinements found nothing: the refinements alone, with the words shown as dropped.
          const only = await this.fetchReply(this.requestParams('', parsed.refinements), signal);
          if (only.items.length) {
            reply = only;
            dropped = parsed.text;
            parsed = { ...parsed, text: '' };
          }
        }
        // Spelling help. Nothing found: try every candidate source (known names, then the
        // API's wildcard) and show the first that has products as "אולי התכוונת ל…?".
        // A few results: offer the closest known name. Many results but a word the engine
        // ignored (absent from every title): offer the corrected phrase.
        let corrected = null;
        const total = reply.totalItems || reply.items.length;
        // After the plain-words fallback the query on screen is the typed text, so the
        // candidates must vary that (keeping the other words: "מרקס אפור" → "מרקש אפור").
        const phrase = fallback ? typed : parsed.text;
        if (phrase && !layout) {
          const options = this.candidates(phrase);
          if (!reply.items.length) {
            let tried = 0;
            const all = [...options];
            if (!all.length) all.push(...(await this.wildcardCandidates(parsed.text, signal)));
            for (const fixed of all) {
              if (tried >= 3 || fixed === parsed.text) break;
              tried += 1;
              const better = await this.fetchReply(this.requestParams(fixed, parsed.refinements), signal);
              if (better.items.length) {
                reply = better;
                corrected = { from: parsed.text, to: fixed, applied: true, kind: 'spelling', alternatives: all.filter((o) => o !== fixed).slice(0, 2) };
                parsed = { ...parsed, text: fixed };
                break;
              }
            }
            if (!corrected && options.length && tried < 3) {
              // The known names led nowhere: the wildcard as the last source.
              for (const fixed of await this.wildcardCandidates(parsed.text, signal)) {
                if (tried >= 3) break;
                tried += 1;
                const better = await this.fetchReply(this.requestParams(fixed, parsed.refinements), signal);
                if (better.items.length) {
                  reply = better;
                  corrected = { from: parsed.text, to: fixed, applied: true, kind: 'spelling', alternatives: [] };
                  parsed = { ...parsed, text: fixed };
                  break;
                }
              }
            }
          } else if (options.length && options[0] !== phrase) {
            const fixed = options[0];
            if (total <= FEW_RESULTS) {
              const better = await this.fetchReply(this.requestParams(fixed, parsed.refinements), signal);
              if ((better.totalItems || better.items.length) > total) corrected = { from: phrase, to: fixed, applied: false, kind: 'spelling', count: better.totalItems };
            } else {
              // Many results, but one typed word appears in no title: the engine skipped it.
              const titles = reply.items.map((r) => r.title.toLowerCase());
              const ignored = phrase.split(/\s+/).some((w) => HEBREW.test(w) && w.length >= 3 && !STOP_HE.has(w) && !this.knownTerms().has(stripFinal(w)) && !titles.some((t) => t.includes(w.toLowerCase())));
              if (ignored) corrected = { from: phrase, to: fixed, applied: false, kind: 'spelling' };
            }
          }
        }
        if (sequence !== this.sequence) return;
        parsed.detected.filter((r) => r.kind === 'size').forEach((r) => this.rememberSize(r.value));
        parsed.refinements.filter((r) => r.kind === 'room').forEach((r) => this.rememberRoom(r.value));
        this.learnBrands(reply.items);
        this.setBusy(false);
        if (layout) corrected = { from: layout.from, to: layout.to, applied: !layout.soldOut, soldOut: !!layout.soldOut, url: layout.url || '', kind: 'layout', alternatives: [] };
        let recovered = null;
        if (original && reply.items.length) recovered = layout ? 'layout' : corrected?.applied ? 'correction' : dropped ? 'dropped' : fallback ? 'fallback' : 'other';
        if (rule.corrected && !corrected) corrected = { from: rule.corrected.from, to: rule.corrected.to, applied: true, rule: true, kind: 'rule', alternatives: [] };
        // Kept for the result clicks: did a corrected search lead to a product?
        this.correction = corrected?.applied || corrected?.soldOut ? { from: corrected.from, to: corrected.to, kind: corrected.kind } : null;
        this.render({ typed, parsed, reply, fallback, dropped, corrected, source, original, recovered, ruleIds: rule.ruleIds, redirect: rule.redirect });
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[site-search]', error);
        if (sequence !== this.sequence) return;
        this.track('search_error', { text: parsed.text, message: String(error?.message || error).slice(0, 120), stage: 'engine' });
        // The results page already holds the store's own results for this query: keep them.
        const server = this.serverResults();
        if (server) {
          this.setBusy(false);
          this.renderServer(server, parsed, typed);
          return;
        }
        // The store's own search as a stand-in: products only, no filters, counts or suggestions.
        try {
          const reply = await this.fallbackReply(parsed.text || typed, signal);
          if (sequence !== this.sequence) return;
          this.setBusy(false);
          this.render({ typed, parsed, reply, fallback: false, dropped: false, corrected: null, source, original: null, recovered: null, ruleIds: rule.ruleIds, redirect: null, degraded: true });
          this.track('search_fallback', { text: parsed.text, count: reply.items.length });
        } catch (fallbackError) {
          if (fallbackError.name === 'AbortError' || sequence !== this.sequence) return;
          this.setBusy(false);
          this.renderError();
        }
      }
    }

    /** Page mode: the results the server put in the HTML, when they are for the query on screen. */
    serverResults() {
      const node = this.main?.querySelector('[data-ss-server]');
      return node && (node.dataset.ssServerQuery || '').trim() === this.query.trim() ? node : null;
    }

    /** The engine failed on the results page: its own results stay, with a notice above. */
    renderServer(server, parsed, typed) {
      this.pageQuick = null;
      const notice = el('p', 'ss__notice', this.labels.degraded || 'החיפוש המלא לא זמין כרגע, אז אלה תוצאות בסיסיות.');
      notice.setAttribute('role', 'status');
      const total = parseInt(server.dataset.ssServerTotal, 10) || server.querySelectorAll('.pc').length;
      this.place({ mode: 'results', rail: [], main: [notice, server], phone: [notice, server] });
      this.setStatus(fill(this.labels.resultsStatus, { count: total, query: typed }));
      if (this.page) this.syncPage(parsed.refinements, parsed.text, total, typed, null);
      this.track('search_fallback', { text: parsed.text, count: total, source: 'server' });
    }

    /**
     * The store's own search (templates/search.ss.liquid: /search?view=ss) as a stand-in when
     * the engine fails — Shopify's predictive endpoint refuses the Hebrew locale. Products
     * only: no filters, counts or suggestions.
     */
    async fallbackReply(text, signal) {
      const url = new URL(`${window.Shopify?.routes?.root || '/'}search`, window.location.origin);
      url.searchParams.set('q', text);
      url.searchParams.set('type', 'product');
      url.searchParams.set('view', 'ss');
      url.searchParams.set('options[prefix]', 'last');
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const items = (Array.isArray(data?.items) ? data.items : []).filter((p) => p && p.link && p.title).slice(0, FETCH_PAGE).map((p) => ({
        rating: 0, reviews: 0, sizes: 0, sizeOptions: [], stock: NaN, family: '', traits: [], variants: [], priceMax: p.price,
        ...p,
        id: String(p.id),
        soldOut: p.available === false,
      }));
      return { totalItems: Number(data?.total) || items.length, items, suggestions: [], categories: [], facets: [] };
    }

    async loadMore() {
      if (!this.view || this.loading) return;
      const { parsed, reply, fallback, typed } = this.view;
      const button = this.main.querySelector('[data-ss-load-more]');
      this.loading = true;
      if (button) button.disabled = true;
      try {
        const refinements = fallback ? this.picked : parsed.refinements;
        let items;
        if (this.buffer.length) {
          items = this.buffer.splice(0, PAGE_SHOWN); // already fetched and ranked
        } else {
          const more = await this.fetchReply(this.requestParams(fallback ? typed : parsed.text, refinements, this.fetched), this.request?.signal);
          if (this.view?.reply !== reply) return;
          this.fetched += more.items.length;
          const ranked = this.rerank(more.items, parsed.text);
          items = ranked.slice(0, PAGE_SHOWN);
          this.buffer = ranked.slice(PAGE_SHOWN);
          this.learnBrands(more.items);
        }
        const grid = this.main.querySelector('.ss__group--products .ss__grid');
        const moreBlock = this.main.querySelector('.ss__more');
        if (!grid || !moreBlock) return;
        const sizeMatch = this.sizeFor(refinements);
        items.forEach((record) => {
          grid.append(this.card(record, { position: this.shown.length, sizeMatch }));
          this.shown.push(record);
        });
        moreBlock.hidden = (!this.buffer.length && this.fetched >= reply.totalItems) || !items.length;
        const left = reply.totalItems - this.shown.length;
        if (button && left > 0) button.textContent = `${this.labels.loadMore} (${left.toLocaleString('en-US')})`;
        const counter = moreBlock.querySelector('.ss__more-count');
        if (counter) counter.textContent = fill(this.labels.showing, { shown: this.shown.length.toLocaleString('en-US'), total: Number(reply.totalItems).toLocaleString('en-US') });
        this.track('search_load_more', { shown: this.shown.length, ...this.rankContext(), impressions: items.map((r) => r.id), from: this.shown.length - items.length + 1 });
        if (this.page) this.observeMore();
      } catch (error) {
        if (error.name !== 'AbortError') console.error('[site-search]', error);
      } finally {
        this.loading = false;
        if (button) button.disabled = false;
      }
    }

    setBusy(busy, skeleton = true) {
      this.spinner.hidden = !busy;
      this.sheet?.classList.toggle('is-busy', busy);
      this.progress?.classList.toggle('is-busy', busy);
      this.body.setAttribute('aria-busy', String(busy));
      clearTimeout(this.skeletonTimer);
      if (!busy) return;
      this.setStatus(this.labels.searching);
      // No results on screen yet (first query): a skeleton after a beat, so a fast reply never flickers.
      // (The results page starts with the server's results on screen: those stay until the reply.)
      if (skeleton && !this.view && !this.serverResults()) {
        this.skeletonTimer = setTimeout(() => {
          if (this.view || !this.dialog.open) return;
          const skeleton = el('div', 'ss__skeleton');
          skeleton.setAttribute('aria-hidden', 'true');
          for (let i = 0; i < 4; i += 1) {
            const card = el('div');
            card.append(el('i'), el('i'), el('i'));
            skeleton.append(card);
          }
          this.place({ mode: 'results', rail: [], main: [skeleton], phone: [skeleton] });
        }, 220);
      }
    }

    setStatus(text) {
      this.status.textContent = text || '';
    }

    resultsUrl(refinements = this.activeRefinements(), text = this.parsed().text) {
      const url = new URL(this.dataset.results, window.location.origin);
      url.searchParams.set('q', refinements.length ? text : this.query);
      this.restrictions(refinements).forEach(([attribute, value]) => url.searchParams.set(`rb_${attribute}`, value));
      // intents that filter nothing ("זול" only orders) travel by id; the others come back with rb_tags
      const bare = refinements.filter((r) => r.kind === 'intent' && intentOf(r.value) && !intentOf(r.value).tags.length).map((r) => r.value);
      if (bare.length) url.searchParams.set('intent', bare.join(','));
      if (this.sort) url.searchParams.set('sort', this.sort);
      return url.pathname + url.search;
    }

    /* ---------------------------------------------------------------- layout
       Blocks are built once per render and placed for the viewport: on phones one column in
       reading order; on 768px+ suggestions and refinements go to the rail beside the products. */

    place(blocks) {
      this.blocks = blocks;
      this.layout();
    }

    layout() {
      const b = this.blocks;
      if (!b) return;
      const desktop = this.desktop.matches && !this.menu; // the menu panel is phone-width
      const keep = (list) => (list || []).filter(Boolean);
      const next = desktop ? { rail: keep(b.rail), main: keep(b.main) } : { rail: [], main: keep(b.phone) };
      const reused = new Set([...next.rail, ...next.main]);
      const discarded = [...this.rail.children, ...this.main.children].filter((node) => !reused.has(node));
      this.rail.replaceChildren(...next.rail);
      this.main.replaceChildren(...next.main);
      // Tag managers and session recorders keep references to nodes they saw (a clicked
      // chip, an observed card); every node links to its parent, so one reference would pin
      // the whole discarded render. The theme's helper unlinks the tree (global.js).
      // (A top-level class in a classic script is a global binding, not a window property.)
      const utility = typeof HTMLUpdateUtility !== 'undefined' ? HTMLUpdateUtility : null;
      if (utility && typeof utility.shatter === 'function') discarded.forEach((node) => utility.shatter(node));
      this.rail.hidden = !this.rail.childElementCount;
      this.body.classList.toggle('is-home', b.mode !== 'results');
      // Refinement groups: always open beside the products; on phones behind "עוד" (unless
      // there is no chip row to hide them behind).
      const refine = this.main.querySelector('.ss__refine') || this.rail.querySelector('.ss__refine');
      const inSheet = !desktop && refine && this.sheet && (this.page || this.main.querySelector('.ss__quick'));
      if (inSheet) {
        // Phones: the groups open in the bottom sheet ("סינון" in the chip row).
        refine.classList.add('is-open');
        const old = [...this.sheetBody.children].filter((node) => node !== refine);
        this.sheetBody.replaceChildren(refine);
        if (utility && typeof utility.shatter === 'function') old.forEach((node) => utility.shatter(node));
      } else {
        if (refine) refine.classList.add('is-open');
        if (this.sheet?.open && !this.sheetBody.querySelector('.ss__refine')) this.closeSheet();
        if (!refine) this.sheetBody?.replaceChildren();
      }
      this.updateSheet();
      // The results page on phones: "סינון" sits in the sticky toolbar beside the sort.
      const filterButton = this.toolbar?.querySelector('[data-ss-filter-btn]');
      if (filterButton) filterButton.hidden = !inSheet;
    }

    /* ---------------------------------------------------------------- filter sheet (phones) */

    openSheet() {
      if (!this.sheet || !this.sheetBody.childElementCount) return;
      this.dismissKeyboard();
      this.updateSheet();
      this.sheetBody.scrollTop = 0;
      this.sheet.showModal();
      this.track('search_filters_open', { refinements: this.activeRefinements().length });
    }

    closeSheet() {
      if (this.sheet?.open) this.sheet.close();
    }

    /** The sheet's button carries the live result count; "ניקוי" only when chips are picked. */
    updateSheet() {
      if (!this.sheetApply) return;
      const total = Number(this.reply?.totalItems) || 0;
      const text = total ? fill(this.labels.allRefined, { count: total.toLocaleString('en-US') }) : 'הצגת התוצאות';
      const textEl = this.sheetApply.querySelector('[data-ss-sheet-apply-text]') || this.sheetApply;
      if (textEl.textContent !== text) {
        textEl.textContent = text;
        // A visible "it worked": the count on the button changes with a short pulse.
        if (this.sheet.open) {
          this.sheetApply.classList.remove('is-updated');
          void this.sheetApply.offsetWidth;
          this.sheetApply.classList.add('is-updated');
        }
      }
      // What is picked, at the top of the sheet: tap × to remove.
      const selected = this.sheet.querySelector('[data-ss-sheet-selected]');
      const picked = this.lastRefinements || [];
      if (selected) {
        selected.hidden = !picked.length;
        selected.querySelector('[data-ss-sheet-selected-list]').replaceChildren(picked.length ? this.activePills(picked) : '');
      }
      const reset = this.sheet.querySelector('[data-ss-sheet-reset]');
      if (reset) reset.hidden = !this.picked.length;
      const badge = this.toolbar?.querySelector('[data-ss-filter-count]');
      if (badge) {
        const active = this.activeRefinements().length;
        badge.textContent = String(active);
        badge.hidden = !active;
      }
    }

    /* ---------------------------------------------------------------- rendering: home */

    renderHome() {
      this.view = null;
      this.zero = false;
      if (this.page) {
        const title = document.querySelector('[data-search-title]');
        const eyebrow = document.querySelector('[data-search-eyebrow]');
        const count = document.querySelector('[data-search-count]');
        if (title) title.textContent = title.dataset.empty || title.textContent;
        if (eyebrow) eyebrow.textContent = 'חיפוש';
        if (count) count.hidden = true;
        if (this.toolbar) this.toolbar.hidden = true;
        this.moreObserver?.disconnect();
        document.title = `${title?.dataset.empty || 'חיפוש'} · ${this.dataset.shop || ''}`.replace(/ · $/, '');
        try {
          if (window.location.search) history.replaceState(history.state, '', window.location.pathname);
        } catch (error) {
          // ignore
        }
      }
      this.shown = [];
      this.foot.hidden = true;
      this.setStatus('');
      const b = {};
      const recent = this.readRecent();
      if (recent.length) {
        b.recent = this.group(this.labels.recent, 'ss__group--recent');
        const clear = el('button', 'ss__link', this.labels.clearAll);
        clear.type = 'button';
        clear.dataset.ssForget = '';
        b.recent.querySelector('.ss__group-head').append(clear);
        b.recent.append(this.recentPills(recent));
      }
      const popular = (this.data.popular || []).map((t) => String(t).trim()).filter((t) => t && !recent.includes(t));
      if (popular.length) {
        b.popular = this.group(this.labels.popular, 'ss__group--popular');
        b.popular.append(this.ranked(popular.slice(0, 6)));
      }
      b.rooms = this.roomsGroup();
      b.sizes = this.group(this.labels.sizes, 'ss__group--sizes');
      b.sizes.append(this.sizeChips(this.homeSizes().map((value) => ({ value })), []));
      const viewed = this.readViewed();
      if (viewed.length) {
        b.viewed = this.group(this.labels.viewed, 'ss__group--viewed');
        b.viewed.append(this.grid(viewed.slice(0, 4), 'viewed'));
      }
      b.trending = this.trendingGroup();
      b.visual = this.visualEntry();
      // In the menu, rooms and sizes are already on its first screen: the search starts from searches.
      const phoneHome = this.menu ? [b.visual, b.recent, b.popular, b.viewed, b.trending] : [b.visual, b.recent, b.popular, b.rooms, b.sizes, b.viewed, b.trending];
      this.place({ mode: 'home', rail: [b.visual, b.recent, b.popular], main: [b.rooms, b.sizes, b.viewed, b.trending], phone: phoneHome });
      this.body.scrollTop = 0;
    }

    /* ---------------------------------------------------------------- similar & visual (FLAGS.similar / visualUpload) */

    /** The photo-search entry on the home view: a button and its (hidden) file input. */
    visualEntry() {
      if (!FLAGS.visualUpload || !analyticsOrigin()) return null;
      const block = el('div', 'ss__visual');
      const button = el('button', 'ss__visual-btn');
      button.type = 'button';
      button.dataset.ssVisual = '';
      button.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/></svg>';
      const text = el('span', 'ss__visual-text');
      text.append(el('strong', null, this.labels.visualCta), el('span', null, this.labels.visualHint));
      button.append(text);
      const file = el('input', 'visually-hidden');
      file.type = 'file';
      file.accept = 'image/*';
      file.tabIndex = -1;
      file.dataset.ssVisualFile = '';
      file.setAttribute('aria-hidden', 'true');
      block.append(button, file, el('p', 'ss__visual-note', this.labels.visualPrivacy));
      return block;
    }

    async onVisualFile(input) {
      const file = input.files && input.files[0];
      input.value = '';
      if (!file || !/^image\//.test(file.type)) return;
      this.track('search_visual_upload', { bytes: file.size, type: file.type });
      await this.showSimilar({ title: this.labels.similarPhoto, kind: 'visual' }, async (signal) => {
        const image = await SiteSearch.shrink(file);
        const body = new FormData();
        body.append('image', image, 'search.jpg');
        const response = await fetch(new URL('/api/analytics/search/visual', analyticsOrigin()), { method: 'POST', body, signal: withTimeout(signal, 12000), credentials: 'omit' });
        return response.ok ? response.json() : null;
      });
    }

    /** The photo scaled down on the device (VISUAL_MAX_SIDE, JPEG): faster to send, nothing extra in it (no EXIF/location). */
    static async shrink(file) {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, VISUAL_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close?.();
      return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode'))), 'image/jpeg', 0.82));
    }

    openSimilar(product) {
      if (!product.id || !analyticsOrigin()) return;
      this.open('similar');
      this.dismissKeyboard();
      this.track('search_similar_open', { productId: product.id });
      this.showSimilar({ title: fill(this.labels.similarTitle, { title: product.title }), kind: 'similar', productId: product.id }, async (signal) => {
        const url = new URL('/api/analytics/search/similar', analyticsOrigin());
        url.searchParams.set('productId', product.id);
        url.searchParams.set('limit', '24');
        const response = await fetch(url, { signal: withTimeout(signal, 6000), credentials: 'omit' });
        return response.ok ? response.json() : null;
      });
    }

    /**
     * Products like a product or a photo: the analytics service returns ids by similarity
     * ({ items: [{ id, score }], version }); the cards come from the engine (in stock only), in that order.
     */
    async showSimilar(context, fetchIds) {
      this.request?.abort();
      this.request = new AbortController();
      const { signal } = this.request;
      const sequence = ++this.sequence;
      this.view = null;
      this.foot.hidden = true;
      const block = el('section', 'ss__group ss__group--similar');
      block.append(el('h2', 'ss__intent-title', context.title), el('p', 'ss__recovery-wait', this.labels.similarWait));
      this.place({ mode: 'results', rail: [], main: [block], phone: [block] });
      this.setBusy(true, false); // the view already says it is looking: no skeleton over it
      try {
        const data = await fetchIds(signal);
        const raw = data && (data.data || data);
        const ids = (Array.isArray(raw?.items) ? raw.items : []).map((x) => String(x && (x.id ?? x.productId))).filter((id) => /^\d+$/.test(id) && id !== context.productId).slice(0, 24);
        let items = [];
        if (ids.length) {
          const reply = await this.fetchReply({ ...this.requestParams('', []), maxResults: ids.length, facets: false, suggestions: false, categories: false, 'restrictBy[product_id]': ids.join('|') }, signal);
          items = ids.map((id) => reply.items.find((r) => String(r.id) === id)).filter(Boolean);
        }
        if (sequence !== this.sequence) return;
        block.querySelector('.ss__recovery-wait')?.remove();
        if (!items.length) {
          block.append(el('p', 'ss__none-query', this.labels.similarNone));
        } else {
          this.shown = items;
          block.append(this.grid(items, 'similar'));
        }
        this.setStatus(context.title);
        this.track('search_similar_shown', { kind: context.kind, productId: context.productId || null, impressions: items.map((r) => r.id), version: raw?.version || null });
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        block.querySelector('.ss__recovery-wait')?.remove();
        block.append(el('p', 'ss__none-query', this.labels.similarNone));
        this.track('search_similar_error', { kind: context.kind, message: String(error?.message || error).slice(0, 120) });
      } finally {
        if (sequence === this.sequence) this.setBusy(false);
      }
    }

    roomsGroup() {
      const group = this.group(this.labels.rooms, 'ss__group--rooms');
      group.append(this.tiles(this.roomsByUse().slice(0, 6).map((room) => ({ label: room.label, refinement: { kind: 'room', value: room.tag, label: room.label } }))));
      return group;
    }

    /** ROOMS with the ones this shopper picks first (at most two promoted, the rest in order). */
    roomsByUse() {
      const counts = storage.read(KEYS.rooms) || {};
      const used = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([tag]) => ROOMS.find((r) => r.tag === tag)).filter(Boolean);
      return [...used, ...ROOMS.filter((r) => !used.includes(r))];
    }

    /** The trending products (snippet data), or null when the collection is empty. */
    trendingGroup() {
      const trending = Array.isArray(this.data.trending) ? this.data.trending.filter((p) => p && p.link && p.available !== false) : [];
      if (!trending.length) return null;
      const group = this.group(this.labels.trending, 'ss__group--trending');
      group.append(this.grid(trending.slice(0, 4), 'trending'));
      return group;
    }

    /** Sizes for the zero-query state: the ones this shopper searched most come first. */
    homeSizes() {
      const counts = storage.read(KEYS.sizes) || {};
      const used = Object.entries(counts).filter(([value]) => SIZES.some((s) => s.value === value)).sort((a, b) => b[1] - a[1]).map(([value]) => value);
      const list = [...used.slice(0, 2), ...HOME_SIZES.filter((value) => !used.slice(0, 2).includes(value))];
      return list.slice(0, 8);
    }

    /* ---------------------------------------------------------------- rendering: results */

    render(view) {
      const { typed, parsed, reply, fallback } = view;
      this.zero = !reply.items.length;
      const total = reply.totalItems || reply.items.length;
      const refinements = fallback ? this.picked : parsed.refinements;
      // The first page: fetched FETCH_PAGE, ranked, PAGE_SHOWN drawn, the rest kept for "עוד".
      const ranked = this.rerank(reply.items, fallback ? typed : parsed.text);
      const items = ranked.slice(0, PAGE_SHOWN);
      this.buffer = ranked.slice(PAGE_SHOWN);
      this.fetched = reply.items.length;
      this.view = view;
      this.reply = reply;
      this.shown = items;
      this.optionSeq = 0;

      const refinementValues = refinements.map((r) => `${r.kind}:${r.value}`);
      const context = { text: parsed.text, refinements: refinementValues, source: view.source || 'typed', ruleIds: view.ruleIds || [], detected: parsed.detected.length };
      this.track('search_query', { ...context, fallback });
      if (view.original && view.recovered) {
        // The zero the shopper actually hit, before the dialog rescued it.
        this.track('search_zero_results', { ...context, text: view.original.text, refinements: view.original.refinements, recovered: view.recovered, correctedTo: view.corrected?.to || null, correctionKind: view.corrected?.kind || null, dropped: view.dropped || null });
      }
      const shownFix = view.corrected?.applied || view.corrected?.soldOut ? view.corrected : null;
      this.track('search_results', { ...context, ...this.rankContext(), impressions: items.map((r) => r.id), count: total, zeroResults: total === 0, recovered: view.recovered || null, correctedFrom: shownFix ? shownFix.from : null, correctedTo: shownFix ? shownFix.to : null, correctionKind: shownFix ? shownFix.kind || null : null, correctionSoldOut: !!view.corrected?.soldOut, dropped: view.dropped || null });
      if (!items.length) {
        this.track('search_zero_results', { ...context, recovered: null, correctedTo: shownFix ? shownFix.to : null, correctionKind: shownFix ? shownFix.kind || null : null });
        this.renderNone(view);
        return;
      }

      const b = {};
      b.intent = this.intentBlock(view, refinements, total);
      // A wish the catalogue holds no data for ("שלא משאיר שערות"): said plainly, with the closest real filter.
      (parsed.notes || []).forEach((wish) => {
        if (!b.intent) return;
        const note = el('p', 'ss__notice ss__notice--wish', wish.note);
        if (wish.suggest && !refinements.some((r) => sameRefinement(r, wish.suggest))) {
          const chip = el('button', 'ss__link ss__link--strong', ` ${wish.suggest.label}`);
          chip.type = 'button';
          this.refineAttrs(chip, wish.suggest, false);
          note.append(chip);
        }
        b.intent.append(note);
        this.track('search_wish_unsupported', { wish: wish.id, query: this.query });
      });
      // An intent with a curated collection ("יוקרתי" → שטיחי יוקרה Art): offered above the results.
      const linked = refinements.map((r) => r.kind === 'intent' && intentOf(r.value)).find((intent) => intent && intent.link);
      if (linked && b.intent) {
        const link = el('a', 'ss__redirect');
        link.href = linked.link.href;
        link.dataset.ssIntentLink = linked.id;
        link.append(icon(ARROW_ICON), el('span', null, linked.link.label));
        b.intent.prepend(link);
      }
      if (view.degraded) {
        const notice = el('p', 'ss__notice', this.labels.degraded || 'החיפוש המלא לא זמין כרגע, אז אלה תוצאות בסיסיות.');
        notice.setAttribute('role', 'status');
        const wrap = el('div');
        wrap.append(notice);
        if (b.intent) wrap.append(b.intent);
        b.intent = wrap;
      }
      const suggestions = this.suggestionRows(view, refinements);
      const categories = this.categoriesFor(reply.categories, parsed.text);
      if (suggestions.length || categories.length) {
        b.sugg = el('div', 'ss__sugg');
        if (suggestions.length) {
          const group = this.group(this.labels.suggestions, 'ss__group--suggest');
          group.append(this.rows(suggestions));
          b.sugg.append(group);
        }
        if (categories.length) {
          const group = this.group(this.labels.categories, 'ss__group--categories');
          group.append(this.rows(categories.map((c) => ({ text: c.title, href: c.link, category: true, local: c.local, icon: ARROW_ICON, match: c.local ? parsed.text.split(/\s+/).pop() : '' }))));
          b.sugg.append(group);
        }
      }
      const chips = this.refinementChips(reply, refinements, parsed);
      if (chips.groups.length) {
        b.refine = el('div', 'ss__refine');
        chips.groups.forEach((group) => b.refine.append(group));
        if (chips.quick.length) {
          b.quick = el('div', 'ss__quick');
          const list = el('ul', 'ss__chips');
          list.setAttribute('role', 'list');
          chips.quick.forEach((chip) => {
            const li = el('li');
            li.append(chip);
            list.append(li);
          });
          const li = el('li');
          const more = el('button', 'ss__chip ss__chip--more');
          more.type = 'button';
          more.dataset.ssMoreChips = '';
          more.setAttribute('aria-haspopup', 'dialog');
          more.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/></svg>';
          more.append(document.createTextNode(refinements.length ? fill(this.labels.moreActive, { count: refinements.length }) : this.labels.more));
          li.append(more);
          list.append(li);
          b.quick.append(list);
        }
      }
      if (!this.page && !fallback && FLAGS.dialogModel) {
        // The dialog: a model name ("סידני") offers its collection above the products, compact.
        const model = this.modelFor(ranked, total, refinements, parsed.text);
        if (model) {
          b.hero = this.modelHero(model, true);
          // the card is that collection: its category row would say it twice
          b.sugg?.querySelectorAll('.ss__group--categories [data-ss-category]').forEach((row) => {
            if (row.textContent.trim() === model.family || row.getAttribute('href')?.includes(`/collections/${model.handle}`)) row.closest('li').remove();
          });
          b.sugg?.querySelectorAll('.ss__group--categories').forEach((group) => { if (!group.querySelector('li')) group.remove(); });
          this.track('search_model_shown', { family: model.family, query: this.query, area: 'dialog' });
        }
      }
      if (this.page && !fallback) {
        // A model name ("סידני"): its collection first, and a short list needs no filters or sorting.
        const model = this.modelFor(ranked, total, refinements, parsed.text);
        if (model) {
          b.hero = this.modelHero(model);
          if (total <= SORT_MIN) {
            b.refine = null;
            b.quick = null;
          }
        } else if (!refinements.length && total >= BROAD_MIN) {
          // A broad search ("שטיח"): rooms and styles to start from, above hundreds of products.
          b.shortcuts = this.shortcutsBlock();
        }
        // Sorting only where there is a long list to reorder; it sits with the filters (sheet / rail).
        if (total > SORT_MIN && (b.refine || !b.hero)) {
          if (!b.refine) b.refine = el('div', 'ss__refine');
          b.refine.append(this.sortGroup());
        }
        // The pills of what the search understood get a "clear all" beside them.
        const pills = b.intent?.querySelector('.ss__active');
        if (pills && refinements.length > 1) {
          const li = el('li');
          const clear = el('button', 'ss__link ss__active-clear', this.labels.clearFilters);
          clear.type = 'button';
          clear.dataset.ssClearFilters = '';
          li.append(clear);
          pills.append(li);
        }
      }
      // Very few results with refinements: the same counted choices, above the products.
      if (FLAGS.recovery && total > 0 && total <= LOW_RESULTS && !fallback && refinements.length) {
        b.recovery = el('div', 'ss__recovery-slot ss__recovery-slot--few');
        this.fillRecovery(b.recovery, view, this.labels.recoverMore);
      }
      b.products = el('section', 'ss__group ss__group--products');
      b.products.append(this.grid(items, 'results', this.sizeFor(refinements)));
      b.more = el('div', 'ss__more');
      if (this.page) b.more.append(el('p', 'ss__more-count', fill(this.labels.showing, { shown: items.length.toLocaleString('en-US'), total: total.toLocaleString('en-US') })));
      const moreButton = el('button', 'ss__more-btn', total > items.length ? `${this.labels.loadMore} (${(total - items.length).toLocaleString('en-US')})` : this.labels.loadMore);
      moreButton.type = 'button';
      moreButton.dataset.ssLoadMore = '';
      b.more.append(moreButton);
      b.more.hidden = !this.buffer.length && this.fetched >= total;

      this.lastRefinements = refinements;
      // Results page on phones: the quick chips ride in the sticky bar beside "סינון" (syncPage).
      this.pageQuick = this.page ? b.quick || null : null;
      this.place({
        mode: 'results',
        rail: [b.sugg, b.refine],
        main: [b.intent, b.hero, b.shortcuts, b.recovery, b.products, b.more],
        phone: this.page ? [b.intent, b.hero, b.shortcuts, b.refine, b.recovery, b.products, b.more] : [b.intent, b.hero, b.sugg, b.quick, b.refine, b.recovery, b.products, b.more],
      });
      if (this.page) {
        this.autoPages = 0;
        this.observeMore();
      } else if (this.restoreScroll) {
        this.body.scrollTop = this.restoreScroll;
        this.restoreScroll = 0;
      } else {
        this.body.scrollTop = 0;
      }
      if (this.page) {
        this.syncPage(refinements, fallback ? typed : parsed.text, total, typed, view.corrected?.applied ? view.corrected.to : null);
      } else {
        // Stand-in results: the store's own search page, since the engine's results page is down too.
        this.allLink.href = view.degraded ? `/search?q=${encodeURIComponent(parsed.text || typed)}&type=product` : this.resultsUrl(refinements, fallback ? typed : parsed.text);
        this.allLink.textContent = total === 1 ? this.labels.one : fill(refinements.length ? this.labels.allRefined : this.labels.all, { count: total.toLocaleString('en-US') });
        this.foot.hidden = false;
      }
      this.setStatus(fill(this.labels.resultsStatus, { count: total, query: typed }));
    }

    /**
     * Page mode: the URL, the heading, the count and the sticky bar follow the search.
     * The H1 is what the shopper searched (or the correction shown); what the search understood
     * is the removable pills under it (intentBlock).
     */
    syncPage(refinements, text, total, typed, override = null) {
      if (this.toolbar) {
        const chips = this.pageQuick || null;
        this.pageQuick = null;
        const old = [...(this.toolbarChips?.children || [])].filter((node) => node !== chips);
        this.toolbarChips?.replaceChildren(...(chips ? [chips] : []));
        const utility = typeof HTMLUpdateUtility !== 'undefined' ? HTMLUpdateUtility : null;
        if (utility && typeof utility.shatter === 'function') old.forEach((node) => utility.shatter(node));
        const filterButton = this.toolbar.querySelector('[data-ss-filter-btn]');
        this.toolbar.hidden = !(chips || (filterButton && !filterButton.hidden));
      }
      try {
        const url = this.resultsUrl(refinements, text);
        if (url !== window.location.pathname + window.location.search) history.replaceState(history.state, '', url);
      } catch (error) {
        // history blocked: the page still works
      }
      const title = document.querySelector('[data-search-title]');
      const eyebrow = document.querySelector('[data-search-eyebrow]');
      const count = document.querySelector('[data-search-count]');
      const words = override || typed;
      const label = words ? fill(this.labels.pageTitle, { query: words }) : [this.labels.rugs, ...refinements.map((r) => this.describe(r))].join(' ');
      if (title) title.textContent = label;
      if (eyebrow) eyebrow.textContent = 'חיפוש';
      if (count) {
        count.textContent = total === 1 ? this.labels.intentOne : fill(this.labels.intentResults, { count: total.toLocaleString('en-US') });
        count.hidden = false;
      }
      document.title = `${words || label} · ${this.dataset.shop || ''}`.replace(/ · $/, '');
    }

    /**
     * The collection a model-name search is about ("סידני" → שטיחי סידני): most of the first
     * results share one family (their related_category tag) and the words name it.
     */
    modelFor(records, total, refinements, text) {
      if (!text || refinements.length || total > MODEL_MAX || !records.length) return null;
      const counts = new Map();
      records.forEach((r) => { if (r.family && r.familyHandle) counts.set(r.family, (counts.get(r.family) || 0) + 1); });
      const [family, n] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0] || [];
      if (!family || n < Math.ceil(records.length * 0.6)) return null;
      const clean = (t) => t.replace(/["'׳״]/g, '').trim();
      const names = clean(family).split(/\s+/);
      const words = clean(text).split(/\s+/).filter((w) => w.length > 1 && !GENERIC.test(w));
      if (!words.some((w) => names.includes(w))) return null;
      const members = records.filter((r) => r.family === family);
      const prices = members.map((r) => r.price).filter((p) => p > 0);
      return {
        family,
        handle: members[0].familyHandle,
        image: members.find((r) => r.image)?.image || '',
        count: n === records.length ? total : n,
        from: prices.length ? Math.min(...prices) : 0,
      };
    }

    /** The model's collection as a header card: photo, name, designs, from-price, link. */
    modelHero(model, compact = false) {
      const link = el('a', compact ? 'ss__model ss__model--compact' : 'ss__model');
      if (compact) {
        // one of the dialog's options: the arrow keys reach it like a suggestion row
        link.id = this.optionId();
        link.dataset.ssOption = '';
      }
      link.href = `/collections/${encodeURIComponent(model.handle)}?filter.v.availability=1`;
      link.dataset.ssModel = model.family;
      if (model.image) {
        const img = el('img', 'ss__model-img');
        img.src = model.image;
        img.alt = '';
        img.width = 160;
        img.height = 160;
        img.decoding = 'async';
        link.append(img);
      }
      const text = el('span', 'ss__model-text');
      text.append(el('span', 'ss__model-eyebrow', this.labels.modelEyebrow), el('strong', 'ss__model-title', fill(this.labels.modelTitle, { name: model.family })));
      const meta = [fill(this.labels.modelCount, { count: model.count.toLocaleString('en-US') })];
      if (model.from) meta.push(fill(this.labels.modelFrom, { price: Math.round(model.from).toLocaleString('en-US') }));
      text.append(el('span', 'ss__model-meta', meta.join(' · ')));
      const cta = el('span', 'ss__model-cta', this.labels.modelCta);
      cta.append(icon(ARROW_ICON));
      link.append(text, cta);
      return link;
    }

    /**
     * Category rows: the engine's categories, then the store's own rug collections whose name holds
     * every typed word (a word may be cut short while typing: "סיד" → שטיחי סידני). Three at most.
     */
    categoriesFor(fromApi, text) {
      const rows = fromApi.slice(0, 3);
      if (!FLAGS.localCollections || !text || rows.length >= 3 || !this.collections.length) return rows;
      const words = text.replace(/["'׳״]/g, '').split(/\s+/).filter((w) => w.length > 1 && !GENERIC.test(w) && !/\d/.test(w)).map((w) => stripFinal(w.toLowerCase()));
      if (!words.length) return rows;
      const path = (href) => { try { return new URL(href, window.location.origin).pathname.replace(/\/$/, ''); } catch (error) { return href; } };
      const name = (t) => t.replace(/["'׳״*\s]/g, '');
      const seen = new Set(rows.flatMap((c) => [path(c.link), name(c.title)]));
      const scored = [];
      this.collections.forEach((c) => {
        const names = c.title.replace(/["'׳״*]/g, ' ').toLowerCase().split(/\s+/).filter(Boolean).map(stripFinal);
        const hits = words.map((w) => (names.includes(w) ? 2 : names.some((n) => n.startsWith(w)) ? 1 : 0));
        if (hits.includes(0)) return;
        scored.push({ c, score: hits.reduce((a, b) => a + b, 0) * 10 - names.length, count: c.count });
      });
      scored.sort((a, b) => b.score - a.score || b.count - a.count).forEach(({ c }) => {
        const link = `/collections/${c.handle}`;
        if (rows.length >= 3 || seen.has(link) || seen.has(name(c.title))) return;
        seen.add(link);
        seen.add(name(c.title));
        rows.push({ title: c.title, link, local: true });
      });
      return rows;
    }

    /** Rooms and styles as photo cards (snippets/search-shortcuts.liquid): where a broad search starts. */
    shortcutsBlock() {
      const { rooms = [], styles = [] } = this.shortcuts || {};
      const rows = [[this.labels.shortcutsRooms, rooms, 'room'], [this.labels.shortcutsStyles, styles, 'style']].filter(([, list]) => list.length);
      if (!rows.length) return null;
      const block = el('section', 'ss__shortcuts');
      block.setAttribute('aria-label', this.labels.shortcutsTitle);
      block.append(el('p', 'ss__shortcuts-title', this.labels.shortcutsTitle));
      rows.forEach(([title, list, kind]) => {
        const row = el('div', 'ss__shortcut-row');
        row.append(el('p', 'ss__shortcut-sub', title));
        const ul = el('ul', 'ss__shortcut-list');
        ul.setAttribute('role', 'list');
        list.forEach((entry) => {
          const li = el('li');
          const a = el('a', 'ss__shortcut');
          a.href = entry.href;
          a.dataset.ssShortcut = kind;
          const media = el('span', 'ss__shortcut-media');
          if (entry.image) {
            const img = el('img');
            img.src = entry.image;
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            img.width = 160;
            img.height = 160;
            media.append(img);
          }
          a.append(media, el('span', 'ss__shortcut-label', entry.label));
          li.append(a);
          ul.append(li);
        });
        row.append(ul);
        block.append(row);
      });
      return block;
    }

    /** Sorting, with the filters: one choice at a time, the current one marked. */
    sortGroup() {
      const group = this.group(this.labels.sortTitle, 'ss__group--sort');
      const list = el('ul', 'ss__chips');
      list.setAttribute('role', 'list');
      [['', this.labels.sortRelevance], ['sales', this.labels.sortSales], ['price-asc', this.labels.sortPriceAsc], ['price-desc', this.labels.sortPriceDesc], ['new', this.labels.sortNew]].forEach(([value, label]) => {
        const li = el('li');
        const chip = el('button', 'ss__chip', label);
        chip.type = 'button';
        chip.dataset.ssSortValue = value;
        chip.setAttribute('aria-pressed', String(this.sort === value));
        li.append(chip);
        list.append(li);
      });
      group.append(list);
      return group;
    }

    /** "Did we understand?": the meaning of the query, its count, and the active refinements. */
    intentBlock(view, refinements, total) {
      const { typed, parsed, fallback, dropped, corrected, redirect } = view;
      const block = el('div', 'ss__intent');
      if (redirect) {
        // A dashboard redirect: the page the store wants this query to reach, above the results.
        const link = el('a', 'ss__redirect');
        link.href = redirect.url;
        link.dataset.ssRedirect = redirect.id;
        link.append(icon(ARROW_ICON), el('span', null, redirect.label));
        block.append(link);
      }
      const head = el('p', 'ss__intent-title');
      if (corrected?.applied && corrected.rule) {
        head.append(fill(this.labels.showingFor, { query: corrected.to }), ' ', el('span', 'ss__intent-note', fill(this.labels.searched, { query: corrected.from })));
        head.append(' ', el('span', 'ss__intent-count', total === 1 ? this.labels.intentOne : fill(this.labels.intentResults, { count: total.toLocaleString('en-US') })));
        block.append(head);
        if (refinements.length) block.append(this.activePills(refinements));
        return block;
      }
      if (corrected?.applied) {
        // "אולי התכוונת ל־X?" — X is a button that makes the correction the query.
        const button = el('button', 'ss__link ss__link--title');
        button.type = 'button';
        button.dataset.ssSuggest = corrected.to;
        button.dataset.ssSource = 'correction';
        button.textContent = corrected.to;
        head.append(this.labels.didYouMean, button, '?');
        block.append(head);
        if (corrected.kind === 'layout') {
          // The keyboard was on English: say so, and keep the words as typed one tap away.
          const sub = el('p', 'ss__intent-note ss__intent-sub ss__intent-layout');
          const asTyped = el('button', 'ss__link');
          asTyped.type = 'button';
          asTyped.dataset.ssSuggest = corrected.from;
          asTyped.dataset.ssSource = 'as-typed';
          asTyped.textContent = fill(this.labels.searchAsTyped || 'לחפש "{from}" כפי שהוקלד', { from: corrected.from });
          sub.append(fill(this.labels.layoutNote || 'נראה שהמקלדת הייתה באנגלית, אז חיפשנו "{to}".', { to: corrected.to, from: corrected.from }), ' ', asTyped);
          block.append(sub);
        } else {
          block.append(el('p', 'ss__intent-note ss__intent-sub', fill(this.labels.showingInstead, { to: corrected.to, from: corrected.from })));
        }
        if (corrected.alternatives?.length) {
          const alt = el('p', 'ss__didyoumean');
          alt.append(this.labels.or, ' ');
          corrected.alternatives.forEach((text, i) => {
            const other = el('button', 'ss__link ss__link--strong');
            other.type = 'button';
            other.dataset.ssSuggest = text;
            other.dataset.ssSource = 'correction';
            other.textContent = text;
            if (i) alt.append(' · ');
            alt.append(other);
          });
          block.append(alt);
        }
        head.append(' ', el('span', 'ss__intent-count', total === 1 ? this.labels.intentOne : fill(this.labels.intentResults, { count: total.toLocaleString('en-US') })));
        if (refinements.length) block.append(this.activePills(refinements));
        return block;
      }
      if (dropped) {
        head.append([this.labels.rugs, ...refinements.map((r) => this.describe(r))].join(' '), ' ', el('span', 'ss__intent-note', fill(this.labels.without, { query: dropped })));
      } else if (fallback) {
        head.textContent = fill(this.labels.showingFor, { query: typed });
      } else if (parsed.nearest && !parsed.nearest.exact) {
        const size = sizeLabel(refinements.find((r) => r.kind === 'size')?.value || '');
        head.append(parsed.text ? `${parsed.text} ` : '', fill(this.labels.intentSize, { size }), ' ', el('span', 'ss__intent-note', `(${fill(this.labels.intentNearest, { typed: parsed.nearest.typed })})`));
      } else if (refinements.length) {
        head.textContent = [parsed.text || this.labels.rugs, ...refinements.map((r) => this.describe(r))].join(' ');
        head.classList.add('ss__intent-title--plain');
      } else {
        head.textContent = typed;
        head.classList.add('ss__intent-title--plain');
      }
      head.append(' ', el('span', 'ss__intent-count', total === 1 ? this.labels.intentOne : fill(this.labels.intentResults, { count: total.toLocaleString('en-US') })));
      block.append(head);
      if (corrected && !corrected.applied) {
        // A few results only: the likely spelling as a one-tap alternative.
        const hint = el('p', 'ss__didyoumean');
        const button = el('button', 'ss__link ss__link--strong');
        button.type = 'button';
        button.dataset.ssSuggest = corrected.to;
        button.dataset.ssSource = 'correction';
        button.textContent = corrected.to;
        hint.append(this.labels.didYouMean, ' ', button, corrected.count ? el('span', 'ss__intent-note', ` (${corrected.count.toLocaleString('en-US')})`) : '');
        block.append(hint);
      }
      if (refinements.length) block.append(this.activePills(refinements));
      return block;
    }

    /** The active refinements as removable pills. */
    activePills(refinements) {
      const list = el('ul', 'ss__active');
      list.setAttribute('role', 'list');
      refinements.forEach((refinement) => {
        const li = el('li');
        const pill = el('button', 'ss__pill');
        pill.type = 'button';
        pill.dataset.ssRemove = refinement.value;
        pill.dataset.ssKind = refinement.kind;
        pill.setAttribute('aria-label', fill(this.labels.remove, { label: refinement.label }));
        pill.append(el('span', null, refinement.label), el('span', 'ss__pill-x', '×'));
        li.append(pill);
        list.append(li);
      });
      return list;
    }

    /** A refinement as a phrase in the intent line ("לסלון", "במידה 160×230"). */
    describe(refinement) {
      if (refinement.kind === 'size') return `במידה ${refinement.label}`;
      if (refinement.kind === 'room') return `ל${refinement.label}`;
      if (refinement.kind === 'shape') return { עגול: 'עגולים', ראנר: 'ראנר', מלבן: 'מלבניים' }[refinement.label] || refinement.label;
      if (refinement.kind === 'color') return `בצבע ${refinement.label}`;
      if (refinement.kind === 'price') return refinement.label;
      if (refinement.kind === 'intent') return intentOf(refinement.value)?.phrase || refinement.label;
      if (refinement.kind === 'sizeClass') return refinement.value === 'large' ? 'גדולים' : 'קטנים';
      if (refinement.kind === 'priceMin') return refinement.label;
      return refinement.label;
    }

    /**
     * The next refinements to offer, from the reply's facets (sizes, colours, shapes with
     * counts) and the catalogue's tags — only kinds not yet chosen, so each pick narrows.
     * Returns the rail groups and the few chips for the first phone screen.
     */
    refinementChips(reply, refinements, parsed) {
      const has = (kind) => refinements.some((r) => r.kind === kind);
      const facet = (attribute) => reply.facets.find((f) => f.attribute === attribute)?.buckets || [];
      const groups = [];
      const quick = [];
      let total = 0;
      const pushGroup = (title, list, className) => {
        if (!list.childElementCount) return;
        const group = this.group(title, className);
        group.append(list);
        groups.push(group);
        total += list.childElementCount;
      };

      // Sizes: the store's sizes for this query, most stocked first (nearest ones when a size is set).
      const sizeBuckets = facet(this.attrs.size).filter((b) => b.count > 0 && b.value !== 'OS');
      if (sizeBuckets.length) {
        let list = sizeBuckets;
        if (has('size')) {
          const current = SIZES.find((s) => s.value === refinements.find((r) => r.kind === 'size').value);
          if (current) list = sizeBuckets.filter((b) => b.value !== current.value).map((b) => ({ ...b, d: this.sizeDistance(current, b.value) })).sort((a, b) => a.d - b.d).slice(0, 4);
        } else if (parsed.nearest) {
          const wanted = parsed.nearest.alternatives.map((s) => s.value);
          list = [...sizeBuckets.filter((b) => wanted.includes(b.value)), ...sizeBuckets.filter((b) => !wanted.includes(b.value))].slice(0, 6);
        } else {
          list = sizeBuckets.slice(0, 6);
        }
        const chips = this.sizeChips(list, refinements);
        pushGroup(this.labels.refineSize, chips, 'ss__group--sizes');
        if (!has('size')) quick.push(...[...chips.querySelectorAll('[data-ss-refine]')].slice(0, 2));
      }
      // Rooms, then styles and finishes — the order a shopper narrows in.
      if (!has('room')) {
        const chips = this.chipList(this.roomsByUse().slice(0, 5).map((room) => ({ kind: 'room', value: room.tag, label: room.label })), refinements);
        pushGroup(this.labels.refineRoom, chips, 'ss__group--rooms');
        quick.unshift(...[...chips.querySelectorAll('[data-ss-refine]')].slice(0, 2));
      }
      const styleChips = this.chipList(
        [...STYLES.filter((s) => !refinements.some((r) => r.value === s.tag)).slice(0, has('style') ? 3 : 4).map((s) => ({ kind: 'style', value: s.tag, label: s.label })), ...FEATURES.filter((f) => !refinements.some((r) => r.value === f.tag)).slice(0, 3).map((f) => ({ kind: 'feature', value: f.tag, label: f.label }))],
        refinements
      );
      pushGroup(this.labels.refineStyle, styleChips, 'ss__group--styles');
      if (has('room') || !quick.length) quick.push(...[...styleChips.querySelectorAll('[data-ss-refine]')].slice(0, 2));
      // Shapes from the facet (only the ones this query has).
      const shapeBuckets = facet(this.attrs.shape).filter((b) => b.count > 0 && b.value !== 'מלבן');
      if (shapeBuckets.length && !has('shape')) {
        const chips = this.chipList(shapeBuckets.slice(0, 3).map((b) => ({ kind: 'shape', value: b.value, label: b.value, count: b.count })), refinements);
        pushGroup(this.labels.refineShape || 'צורה', chips, 'ss__group--shapes');
      }
      // Colours as swatches from the facet.
      const colourBuckets = facet(this.attrs.color).filter((b) => b.count > 0);
      if (colourBuckets.length && !has('color')) {
        pushGroup(this.labels.refineColor, this.colourChips(colourBuckets.slice(0, 8), refinements), 'ss__group--colours');
      }
      if (!has('price')) {
        const chips = this.chipList(PRICES.map((price) => ({ kind: 'price', value: String(price), label: fill(this.labels.priceUpTo, { price: price.toLocaleString('en-US') }) })), refinements);
        pushGroup(this.labels.refinePrice, chips, 'ss__group--price');
        if (quick.length < QUICK_MAX) quick.push(chips.querySelector('[data-ss-refine]'));
      }
      // The phone's first screen shows a few chips (copies: the originals stay in the groups).
      const picks = quick.filter(Boolean).slice(0, QUICK_MAX).map((chip) => {
        const copy = el('button', 'ss__chip');
        copy.type = 'button';
        copy.append(el('span', null, chip.dataset.ssLabel));
        this.refineAttrs(copy, { kind: chip.dataset.ssKind, value: chip.dataset.ssRefine, label: chip.dataset.ssLabel }, false);
        return copy;
      });
      return { groups, quick: total > QUICK_MAX + 1 ? picks : [], total };
    }

    sizeDistance(size, value) {
      const other = SIZES.find((s) => s.value === value);
      if (!other) return Infinity;
      return Math.abs(size.w - other.w) + Math.abs(size.h - other.h);
    }

    /* ---------------------------------------------------------------- rendering: zero results */

    renderNone(view) {
      const { typed, parsed } = view;
      const refinements = parsed.refinements;
      const b = {};
      b.intent = el('div', 'ss__none');
      const layoutMiss = view.corrected?.kind === 'layout' && view.corrected.soldOut ? view.corrected : null;
      if (layoutMiss) {
        // Wrong keyboard, and the model it spells is sold out: name it, link it, then the tries.
        const title = el('p', 'ss__none-title');
        title.append(this.labels.didYouMean, el('strong', null, layoutMiss.to), '?');
        const note = el('p', 'ss__none-query', fill(this.labels.layoutSoldOut || 'נראה שהמקלדת הייתה באנגלית. "{to}" אזל כרגע מהמלאי, אז הנה שטיחים שאולי יתאימו לכם.', { to: layoutMiss.to }));
        b.intent.append(title, note);
        if (layoutMiss.url) {
          const link = el('a', 'ss__link ss__none-model', fill(this.labels.layoutModel || 'לעמוד הדגם {to}', { to: layoutMiss.to }));
          link.href = layoutMiss.url;
          link.dataset.ssLayoutModel = '';
          b.intent.append(link);
        }
      } else {
        b.intent.append(el('p', 'ss__none-title', this.labels.none), el('p', 'ss__none-query', fill(this.labels.noneSearched, { query: typed })));
      }
      const tries = [];
      // refinements: offered by the recovery panel below, with real counts (fillRecovery); flag off → the plain "without" chips
      if (!FLAGS.recovery) refinements.forEach((r) => tries.push({ label: fill(this.labels.noneWithout, { label: r.label }), remove: r }));
      const words = parsed.text.split(/\s+/).filter((w) => w && !GENERIC.test(w));
      if (words.length > 1) {
        tries.push({ label: words.slice(0, -1).join(' '), suggest: words.slice(0, -1).join(' ') });
        tries.push({ label: words[words.length - 1], suggest: words[words.length - 1] });
      }
      if (parsed.nearest) parsed.nearest.alternatives.slice(0, 2).forEach((s) => tries.push({ label: sizeLabel(s.value), refinement: { kind: 'size', value: s.value, label: sizeLabel(s.value) } }));
      const colour = refinements.find((r) => r.kind === 'color');
      (colour ? NEAR_COLOURS[colour.value] || [] : []).slice(0, 2).forEach((name) => tries.push({ label: name, refinement: { kind: 'color', value: name, label: name } }));
      const fixed = parsed.text ? this.correct(parsed.text) : '';
      if (fixed && fixed !== parsed.text) tries.unshift({ label: fill(this.labels.maybe, { query: fixed }), suggest: fixed });
      (this.data.popular || []).slice(0, 3).forEach((text) => tries.push({ label: text, suggest: text }));
      if (FLAGS.recovery && refinements.length) {
        const slot = el('div', 'ss__recovery-slot');
        slot.append(el('p', 'ss__recovery-wait', this.labels.recoverWait));
        b.intent.append(slot);
        this.fillRecovery(slot, view, this.labels.recoverTitle);
      }
      if (tries.length) {
        const group = this.group(this.labels.noneTry, 'ss__group--try');
        const list = el('ul', 'ss__chips');
        list.setAttribute('role', 'list');
        tries.slice(0, 7).forEach((entry) => {
          const li = el('li');
          const chip = el('button', 'ss__chip', entry.label);
          chip.type = 'button';
          chip.id = this.optionId();
          chip.dataset.ssOption = '';
          if (entry.remove) {
            chip.dataset.ssRemove = entry.remove.value;
            chip.dataset.ssKind = entry.remove.kind;
          } else if (entry.refinement) {
            this.refineAttrs(chip, entry.refinement, false);
          } else {
            chip.dataset.ssSuggest = entry.suggest;
            chip.dataset.ssSource = 'recovery';
          }
          li.append(chip);
          list.append(li);
        });
        group.append(list);
        b.intent.append(group);
      }
      // The rug quiz: for a search that found nothing, six questions instead of more guessing.
      const quiz = el('a', 'ss__quiz-link');
      quiz.href = '/pages/quiz?from=search';
      quiz.append(el('b', null, 'לא מוצאים? מצאו שטיח ב־60 שניות'), el('span', null, 'כמה שאלות קצרות על החדר והמידה, ונציג את השטיחים שמתאימים לכם'));
      b.intent.append(quiz);
      b.shortcuts = this.page ? this.shortcutsBlock() : null;
      b.rooms = b.shortcuts ? null : this.roomsGroup();
      b.trending = this.trendingGroup();
      this.pageQuick = null;
      this.place({ mode: 'results', rail: [b.rooms], main: [b.intent, b.shortcuts, b.trending], phone: [b.intent, b.shortcuts, b.rooms, b.trending] });
      if (this.page) this.syncPage(refinements, parsed.text, 0, typed);
      else this.body.scrollTop = 0;
      this.foot.hidden = true;
      this.setStatus(this.labels.none);
    }

    renderError() {
      const box = el('div', 'ss__error');
      box.setAttribute('role', 'alert');
      box.append(el('p', 'ss__error-text', this.labels.error));
      const retry = el('button', 'ss__error-btn', this.labels.errorRetry || 'נסו שוב');
      retry.type = 'button';
      retry.dataset.ssRetry = '';
      box.append(retry);
      const links = el('p', 'ss__error-links');
      const all = el('a', 'ss__link', this.labels.errorBrowse || 'לכל השטיחים');
      all.href = '/collections/all';
      links.append(all);
      box.append(links);
      this.place({ mode: 'results', rail: [], main: [box], phone: [box] });
      this.foot.hidden = true;
      this.setStatus(this.labels.error);
    }

    /* ---------------------------------------------------------------- building blocks */

    optionId() {
      this.optionSeq += 1;
      return `ss-opt-${this.optionSeq}`;
    }

    group(title, className = '') {
      const group = el('section', `ss__group ${className}`.trim());
      const head = el('div', 'ss__group-head');
      head.append(el('h2', 'ss__title', title));
      group.append(head);
      return group;
    }

    /** Rows: recent/popular/suggestions (fill the field) or category links. */
    rows(entries) {
      const list = el('ul', 'ss__rows');
      list.setAttribute('role', 'list');
      entries.forEach((entry) => {
        const li = el('li');
        let row;
        if (entry.href) {
          row = el('a', 'ss__row');
          row.href = entry.href;
          row.dataset.ssCategory = entry.local ? 'store' : '';
        } else if (entry.refinement) {
          row = el('button', 'ss__row ss__row--intent');
          row.type = 'button';
          this.refineAttrs(row, entry.refinement, false);
        } else {
          row = el('button', 'ss__row');
          row.type = 'button';
          row.dataset.ssSuggest = entry.suggest;
          row.dataset.ssSource = entry.source || '';
        }
        if (!row.id) row.id = this.optionId();
        row.dataset.ssOption = '';
        row.append(icon(entry.icon || SEARCH_ICON));
        const label = el('span', 'ss__row-text');
        const index = entry.match ? entry.text.toLowerCase().indexOf(entry.match.toLowerCase()) : -1;
        if (index >= 0) {
          // Only the typed part is emphasised — the rest of the phrase stays plain.
          label.append(entry.text.slice(0, index), el('strong', null, entry.text.slice(index, index + entry.match.length)), entry.text.slice(index + entry.match.length));
        } else {
          label.textContent = entry.text;
        }
        row.append(label);
        if (entry.count) row.append(el('span', 'ss__row-count', entry.count.toLocaleString('en-US')));
        li.append(row);
        if (entry.forget) {
          const forget = el('button', 'ss__row-x');
          forget.type = 'button';
          forget.dataset.ssForget = entry.forget;
          forget.setAttribute('aria-label', fill(this.labels.forget, { query: entry.forget }));
          forget.textContent = '×';
          li.append(forget);
        }
        list.append(li);
      });
      return list;
    }

    /** Recent searches as pills: the text searches again, the × forgets it. */
    recentPills(list) {
      const ul = el('ul', 'ss__recent');
      ul.setAttribute('role', 'list');
      list.forEach((text) => {
        const li = el('li');
        const chip = el('button', 'ss__chip ss__chip--recent');
        chip.type = 'button';
        chip.id = this.optionId();
        chip.dataset.ssOption = '';
        chip.dataset.ssSuggest = text;
        chip.dataset.ssSource = 'recent';
        chip.append(el('span', null, text));
        const forget = el('button', 'ss__recent-x');
        forget.type = 'button';
        forget.dataset.ssForget = text;
        forget.setAttribute('aria-label', fill(this.labels.forget, { query: text }));
        forget.textContent = '×';
        li.append(chip, forget);
        ul.append(li);
      });
      return ul;
    }

    /** Popular searches as a ranked list (01, 02 …). */
    ranked(list) {
      const ul = el('ul', 'ss__ranked');
      ul.setAttribute('role', 'list');
      list.forEach((text, index) => {
        const li = el('li');
        const row = el('button', 'ss__row ss__row--ranked');
        row.type = 'button';
        row.id = this.optionId();
        row.dataset.ssOption = '';
        row.dataset.ssSuggest = text;
        row.dataset.ssSource = 'popular';
        row.append(el('span', 'ss__rank', String(index + 1).padStart(2, '0')), el('span', 'ss__row-text', text), icon(ARROW_ICON));
        li.append(row);
        ul.append(li);
      });
      return ul;
    }

    refineAttrs(chip, refinement, on) {
      chip.dataset.ssRefine = refinement.value;
      chip.dataset.ssKind = refinement.kind;
      chip.dataset.ssLabel = refinement.label;
      chip.setAttribute('aria-pressed', String(on));
      chip.dataset.ssOption = '';
      if (!chip.id) chip.id = this.optionId();
    }

    chipList(entries, refinements) {
      const list = el('ul', 'ss__chips');
      list.setAttribute('role', 'list');
      entries.forEach((entry) => {
        const li = el('li');
        const chip = el('button', 'ss__chip');
        chip.type = 'button';
        chip.append(el('span', null, entry.label));
        if (entry.count) chip.append(el('span', 'ss__chip-count', entry.count.toLocaleString('en-US')));
        this.refineAttrs(chip, entry, refinements.some((r) => sameRefinement(r, entry)));
        li.append(chip);
        list.append(li);
      });
      return list;
    }

    sizeChips(buckets, refinements) {
      const list = el('ul', 'ss__sizes');
      list.setAttribute('role', 'list');
      buckets.forEach((bucket) => {
        const li = el('li');
        const chip = el('button', 'ss__size');
        chip.type = 'button';
        chip.append(el('strong', null, sizeLabel(bucket.value)));
        const family = sizeFamily(bucket.value);
        if (family) chip.append(el('span', null, family));
        this.refineAttrs(chip, { kind: 'size', value: bucket.value, label: sizeLabel(bucket.value) }, refinements.some((r) => r.kind === 'size' && r.value === bucket.value));
        li.append(chip);
        list.append(li);
      });
      return list;
    }

    colourChips(buckets, refinements) {
      const list = el('ul', 'ss__chips ss__chips--colours');
      list.setAttribute('role', 'list');
      buckets.forEach((bucket) => {
        const li = el('li');
        const chip = el('button', 'ss__chip ss__chip--colour');
        chip.type = 'button';
        const dot = el('span', 'ss__dot');
        if (bucket.color_code && bucket.color_code !== '*' && /^[0-9a-f]{6}$/i.test(bucket.color_code)) dot.style.background = `#${bucket.color_code}`;
        else dot.classList.add('is-multi');
        chip.append(dot, el('span', null, bucket.title || bucket.value));
        this.refineAttrs(chip, { kind: 'color', value: bucket.value, label: bucket.title || bucket.value }, refinements.some((r) => r.kind === 'color' && r.value === bucket.value));
        li.append(chip);
        list.append(li);
      });
      return list;
    }

    /** Room tiles for the zero-query state: a tap starts a refined search. */
    tiles(entries) {
      const list = el('ul', 'ss__tiles');
      list.setAttribute('role', 'list');
      entries.forEach((entry) => {
        const li = el('li');
        const tile = el('button', 'ss__tile');
        tile.type = 'button';
        tile.append(el('span', null, entry.label));
        this.refineAttrs(tile, entry.refinement, false);
        li.append(tile);
        list.append(li);
      });
      return list;
    }

    grid(records, source, sizeMatch = null) {
      const grid = el('div', 'ss__grid');
      records.forEach((record, position) => grid.append(this.card(record, { position, source, sizeMatch })));
      return grid;
    }

    /** The size to confirm on cards ("קיים ב-…") when the search is about one. */
    sizeFor(refinements) {
      return refinements.find((r) => r.kind === 'size')?.value || null;
    }

    /** An API item as a card record (the snippet gives trending/viewed in the same shape). */
    static fromApi(item) {
      const variants = Array.isArray(item.shopify_variants) ? item.shopify_variants : [];
      const priced = variants.filter((v) => v.available !== '0' && parseFloat(v.price) > 0).sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      const prices = priced.map((v) => parseFloat(v.price));
      const sizes = [];
      variants.forEach((v) => {
        Object.entries(v.options || {}).forEach(([name, value]) => {
          if (SIZE_OPTION.test(name) && !sizes.some((s) => s.value === value)) sizes.push({ value, available: v.available !== '0' });
        });
      });
      return {
        id: item.product_id,
        title: item.title || '',
        link: item.link,
        image: item.image_link,
        // The variants with a SKU: a query that names one opens that variant (pinSku).
        variants: variants.filter((v) => v.sku).map((v) => ({
          id: String(v.variant_id || ''),
          sku: String(v.sku),
          link: v.link || item.link,
          image: v.image_link || '',
          price: parseFloat(v.price) || 0,
          was: parseFloat(v.list_price) || 0,
          available: v.available !== '0',
          size: Object.entries(v.options || {}).find(([name]) => SIZE_OPTION.test(name))?.[1] || '',
        })),
        price: prices.length ? Math.min(...prices) : parseFloat(item.price),
        priceMax: prices.length ? Math.max(...prices) : parseFloat(item.price),
        was: parseFloat(priced.length ? priced[0].list_price : item.list_price) || 0,
        rating: parseFloat(item.reviews_average_score) || 0,
        reviews: parseInt(item.total_reviews, 10) || 0,
        sizes: sizes.filter((s) => s.available).length,
        sizeOptions: sizes,
        stock: parseInt(item.quantity_total, 10),
        // sold out: no variant available (quantity_total comes back empty for these)
        soldOut: sizes.length ? !sizes.some((size) => size.available) : parseInt(item.quantity, 10) === 0,
        family: SiteSearch.familyOf(item.tags),
        familyHandle: SiteSearch.familyHandle(item.tags),
        // Feature / material / colour-group tags only: what the customer intent can match.
        traits: String(item.tags || '').split('[:ATTR:]').map((t) => t.trim()).filter((t) => /^(Features|Material|Color Group)_/.test(t)),
      };
    }

    /** The rug family from the item's tags: "related_category:שטיחי-בוסטון" → "בוסטון". */
    static familyOf(tags) {
      const match = typeof tags === 'string' ? tags.match(/related_category:([^\[]+)/) : null;
      if (!match) return '';
      const name = match[1].replace(/^שטיחי-|^שטיחים-|^שטיח-/, '').replace(/-/g, ' ').trim();
      return HEBREW.test(name.replace(/\s/g, '')) ? name : '';
    }

    /** The family's collection handle from the same tag: "related_category:שטיחי-בוסטון" → "שטיחי-בוסטון". */
    static familyHandle(tags) {
      const match = typeof tags === 'string' ? tags.match(/related_category:([^\[]+)/) : null;
      return match ? match[1].trim() : '';
    }

    /** One product, in the store's card language (product-card.css). */
    card(record, { position = 0, source = 'results', sizeMatch = null } = {}) {
      const link = el('a', 'ss__card pc');
      link.href = record.link;
      link.id = this.optionId();
      link.dataset.ssProduct = '';
      link.dataset.ssOption = '';
      link.dataset.ssId = record.id;
      link.dataset.ssPosition = position;
      link.dataset.ssSource = source;

      const media = el('span', 'pc__media');
      const frame = el('span', 'pc__image ss__card-image');
      if (record.image) {
        const img = document.createElement('img');
        img.src = record.image;
        img.alt = '';
        img.loading = position < 2 ? 'eager' : 'lazy';
        img.decoding = 'async';
        img.width = 400;
        img.height = 400;
        const loaded = () => img.classList.add('is-loaded');
        img.addEventListener('load', loaded, { once: true });
        img.addEventListener('error', loaded, { once: true });
        if (img.complete) loaded();
        frame.append(img);
      }
      // One badge, only from data we hold — the discount (as on the collection cards), else a
      // bestseller (daily top list), else the last units.
      const percent = record.was > record.price && record.was > 0 ? Math.round(((record.was - record.price) / record.was) * 100) : 0;
      if (record.soldOut) media.append(el('span', 'pc__badge ss__card-badge is-out', this.labels.soldOut || 'אזל מהמלאי'));
      else if (percent > 0) media.append(el('span', 'pc__badge ss__card-badge is-sale', fill(this.labels.discount, { percent })));
      else if (this.best.has(String(record.id))) media.append(el('span', 'pc__badge ss__card-badge', this.labels.bestseller));
      else if (record.stock > 0 && record.stock <= LOW_STOCK) media.append(el('span', 'pc__badge ss__card-badge is-low', this.labels.lowStock));
      media.append(frame);
      // Quick add, where the page has a quick view (search page, collections, product page).
      // The card is a link, so the trigger is a button-role span handled in onClick.
      if (!record.soldOut && document.querySelector('quick-view')) {
        const quick = el('span', 'pc__quick');
        quick.setAttribute('role', 'button');
        quick.tabIndex = 0;
        quick.dataset.ssQuick = record.link;
        quick.setAttribute('aria-label', `הוספה מהירה · ${displayTitle(record.title)}`);
        quick.setAttribute('aria-haspopup', 'dialog');
        quick.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5.5 8h13l-1.1 12.1a1 1 0 0 1-1 .9H7.6a1 1 0 0 1-1-.9L5.5 8z"/><path d="M9 8V6.5a3 3 0 0 1 6 0V8M12 11.5v5M9.5 14h5"/></svg>';
        media.append(quick);
      }

      const body = el('span', 'pc__body');
      body.append(el('span', 'ss__card-title', displayTitle(record.title)));
      const price = el('span', 'pc__price');
      const varies = record.priceMax > record.price;
      if (varies) price.append(el('span', 'pc__from', this.labels.from));
      const onSale = record.was > record.price;
      price.append(el('span', `pc__now${onSale ? ' is-sale' : ''}`, money(record.price)));
      if (onSale) price.append(el('s', 'pc__was', money(record.was)));
      body.append(price);

      const meta = el('span', 'pc__meta');
      if (record.rating > 0 && record.reviews > 0) {
        const badge = el('span', 'pc__rating');
        badge.setAttribute('aria-label', fill(this.labels.rating, { rating: record.rating.toFixed(1), count: record.reviews }));
        badge.innerHTML = STAR;
        badge.append(el('strong', null, record.rating.toFixed(1)), el('span', null, `(${record.reviews})`));
        meta.append(badge);
      }
      const match = sizeMatch && Array.isArray(record.sizeOptions) ? record.sizeOptions.find((s) => s.value === sizeMatch && s.available) : null;
      if (record.skuMatch) {
        const ok = el('span', `ss__card-match${record.skuMatch.available ? '' : ' is-out'}`);
        ok.innerHTML = record.skuMatch.available ? CHECK : '';
        ok.append(fill(this.labels.skuMatch || 'מק״ט {sku} · {size}', { sku: record.skuMatch.sku, size: record.skuMatch.size ? sizeLabel(record.skuMatch.size) : '' }).replace(/ · $/, ''));
        meta.append(ok);
      } else if (match) {
        const ok = el('span', 'ss__card-match');
        ok.innerHTML = CHECK;
        ok.append(fill(this.labels.sizeMatch, { size: sizeLabel(match.value) }));
        meta.append(ok);
      } else if (record.sizes > 1) {
        meta.append(el('span', 'pc__sizes', fill(this.labels.sizeCount, { count: record.sizes })));
      }
      if (meta.childElementCount) body.append(meta);

      link.append(media, body);
      return link;
    }

    /* ---------------------------------------------------------------- knowledge
       What the dialog knows beyond the API reply: the store's collection names (spelling
       correction + English↔Hebrew brands), brand pairs learned from titles, the daily
       bestseller list. All loaded in idle time and kept on the device for a day. */

    loadKnowledge() {
      const families = storage.read(KEYS.families);
      if (Array.isArray(families)) families.forEach((name) => typeof name === 'string' && this.families.add(name));
      const literal = storage.read(KEYS.literal);
      if (Array.isArray(literal)) literal.forEach((word) => typeof word === 'string' && LITERAL.add(word));
      this.families.forEach((name) => this.learnLiteral(name));
      (Array.isArray(this.data.trending) ? this.data.trending : []).forEach((p) => p && p.title && this.learnBrands([{ title: p.title }]));
      const brands = storage.read(KEYS.brands);
      if (brands && typeof brands === 'object') Object.entries(brands).forEach(([en, he]) => this.brands.set(en, he));
      const vocab = storage.read(KEYS.vocab);
      // a copy without collections (stored before they were kept) is refreshed once
      if (vocab && Array.isArray(vocab.terms) && Array.isArray(vocab.collections) && Date.now() - (vocab.at || 0) < DAY_MS) {
        this.vocab = vocab.terms;
        this.collections = vocab.collections.filter((c) => c && typeof c.title === 'string' && typeof c.handle === 'string');
        vocab.terms.forEach(([he, en]) => en && !this.brands.has(en) && this.brands.set(en, he));
      } else if (!this.menu) {
        this.whenIdle(() => this.fetchVocabulary()); // the header instance fetches; the menu reads its copy on open
      }
      const best = storage.read(KEYS.best);
      if (best && Array.isArray(best.ids) && Date.now() - (best.at || 0) < DAY_MS) this.best = new Set(best.ids.map(String));
      else if (!this.menu) this.whenIdle(() => this.fetchBestsellers());
    }

    whenIdle(task) {
      if (navigator.connection?.saveData) return;
      if ('requestIdleCallback' in window) requestIdleCallback(() => task(), { timeout: 8000 });
      else setTimeout(task, 3000);
    }

    /** Rug collections → [hebrew name, english handle word]; ~400 collections, two pages. */
    /** The Hebrew words of a catalogue name join LITERAL (see there). */
    learnLiteral(name) {
      String(name || '').replace(/["'׳״*]/g, ' ').split(/\s+/).forEach((w) => {
        if (w.length >= 4 && HEBREW.test(w) && !GENERIC.test(w) && !STOP_HE.has(w)) LITERAL.add(stripFinal(w));
      });
    }

    rememberLiteral(words) {
      words.forEach((w) => LITERAL.add(stripFinal(w)));
      storage.write(KEYS.literal, [...LITERAL].slice(-300));
    }

    async fetchVocabulary() {
      try {
        const terms = [];
        const list = [];
        const seen = new Set();
        for (let page = 1; page <= 2; page += 1) {
          const response = await fetch(`/collections.json?limit=250&page=${page}`, { credentials: 'same-origin' });
          if (!response.ok) break;
          const { collections = [] } = await response.json();
          collections.forEach((c) => {
            if (!c.products_count || !/שטיח/.test(c.title)) return;
            this.learnLiteral(c.title);
            if (c.handle && c.title.length <= 60) list.push({ title: c.title.trim(), handle: c.handle, count: c.products_count });
            const words = c.title.replace(/[*"'׳״]/g, ' ').split(/\s+/).filter((w) => HEBREW.test(w) && !STOP_HE.has(w) && !/\d/.test(w));
            if (!words.length || words.length > 2) return;
            const he = words[0];
            if (seen.has(he)) return;
            seen.add(he);
            const en = (c.handle || '').split(/[-_]/).find((part) => LATIN.test(part) && !STOP_EN.has(part)) || '';
            terms.push([he, en]);
          });
          if (collections.length < 250) break;
        }
        if (!terms.length) return;
        this.vocab = terms;
        this.collections = list;
        terms.forEach(([he, en]) => en && !this.brands.has(en) && this.brands.set(en, he));
        storage.write(KEYS.vocab, { at: Date.now(), terms, collections: list });
      } catch (error) {
        // no vocabulary: no spelling help, search still works
      }
    }

    /** The store's top sellers (product ids), for the "רב מכר" badge. */
    async fetchBestsellers() {
      try {
        const url = new URL(this.dataset.api);
        Object.entries({ api_key: this.dataset.key, q: '', maxResults: 24, items: true, facets: false, suggestions: false, categories: false, pages: false, output: 'json', 'restrictBy[quantity]': '1|', 'restrictBy[product_type]': RUG_TYPE, sortBy: 'sales_amount', sortOrder: 'desc' }).forEach(([k, v]) => url.searchParams.set(k, v));
        const response = await fetch(url);
        if (!response.ok) return;
        const data = await response.json();
        const ids = (data.items || []).map((item) => String(item.product_id)).filter(Boolean);
        if (!ids.length) return;
        this.learnBrands((data.items || []).map(SiteSearch.fromApi));
        this.best = new Set(ids);
        storage.write(KEYS.best, { at: Date.now(), ids });
      } catch (error) {
        // no badge, nothing else changes
      }
    }

    /** Titles carry both names ("שטיח סידני 01 קרם SYDNEY"): remember the pair. */
    learnBrands(records) {
      let changed = false;
      let familiesChanged = false;
      records.forEach((record) => {
        if (record.family && !this.families.has(record.family)) {
          this.families.add(record.family);
          this.learnLiteral(record.family);
          familiesChanged = true;
        }
        const words = record.title.split(/\s+/);
        const en = words.find((w) => /^[A-Z]{3,}$/.test(w));
        const he = words.find((w) => HEBREW.test(w) && !STOP_HE.has(w) && w.length >= 2);
        if (!en || !he) return;
        const key = en.toLowerCase();
        if (this.brands.has(key)) return;
        this.brands.set(key, he);
        changed = true;
      });
      if (changed) {
        const entries = [...this.brands.entries()].slice(-300);
        storage.write(KEYS.brands, Object.fromEntries(entries));
      }
      if (familiesChanged) storage.write(KEYS.families, [...this.families].slice(-400));
      if (changed || familiesChanged) this.terms = null;
    }

    /**
     * The typed text with likely misspellings replaced by the nearest known name (colours,
     * rooms, styles, shapes, collection names, brands, popular searches). Numbers, sizes and
     * words the parser already understands are left alone. Returns '' when nothing changes.
     */
    correct(text) {
      return this.candidates(text)[0] || '';
    }

    /**
     * The text as it reads with the keyboard on Hebrew: { text, from, to } or null. Only words
     * typed with letter keys (and ; , . = ף ת ץ) of three keys or more flip; numbers, sizes
     * ("160x230", "XL"), catalogue numbers and the English names of our collections
     * ("sydney", learned from the titles) stay as typed. Hebrew words in the query are kept.
     */
    layoutFlip(text) {
      if (!text || isSku(text)) return null;
      const from = [];
      const to = [];
      const words = text.trim().split(/\s+/).map((word) => {
        const key = word.toLowerCase();
        if (!LAYOUT_WORD.test(key) || this.brands.has(key) || parseQuery(word, this.brands).detected.length) return word;
        const flipped = [...key].map((c) => LAYOUT_HE[c] || c).join('');
        if (!HEBREW.test(flipped)) return word;
        from.push(word);
        to.push(flipped);
        return flipped;
      });
      return to.length ? { text: words.join(' '), from, to } : null;
    }

    /**
     * The corrected text in the words the catalogue uses: each flipped word becomes the title
     * word it begins ("גניפ" → "ג'ניפר", a partial "סידנ" → "סידני"), the most frequent first.
     */
    namedAs(items, flip) {
      const norm = (w) => stripFinal(String(w).replace(/['"׳״]/g, ''));
      const counts = new Map();
      items.forEach((item) => String(item.title || '').split(/\s+/).forEach((w) => {
        if (HEBREW.test(w)) counts.set(w, (counts.get(w) || 0) + 1);
      }));
      const byWord = new Map(flip.to.map((f) => {
        const base = norm(f);
        let best = f;
        let bestCount = 0;
        counts.forEach((n, w) => {
          const plain = norm(w);
          if (plain.startsWith(base) && plain.length - base.length <= 3 && n > bestCount) {
            best = w;
            bestCount = n;
          }
        });
        return [f, best];
      }));
      return flip.text.split(/\s+/).map((w) => byWord.get(w) || w).join(' ');
    }

    /** Whether any result title holds one of the words (case-insensitive). */
    static mentions(items, words) {
      const titles = items.map((item) => String(item.title || '').toLowerCase());
      return words.some((w) => titles.some((t) => t.includes(String(w).toLowerCase())));
    }

    /** Up to three corrected texts, best first (the first misspelt word varies). */
    candidates(text) {
      const known = this.knownTerms();
      const prefix = (a, b) => { let n = 0; while (n < a.length && n < b.length && a[n] === b[n]) n += 1; return n; };
      const options = []; // for the first token that needs help
      let slot = -1;
      const tokens = text.split(/\s+/);
      tokens.forEach((token, index) => {
        if (slot >= 0 || !token || /\d/.test(token) || STOP_HE.has(token) || GENERIC.test(token)) return;
        if (LATIN.test(token)) {
          const key = token.toLowerCase();
          if (this.brands.has(key)) return;
          const found = [];
          this.brands.forEach((he, en) => {
            const d = editDistance(key, en, 1);
            if (d <= 1) found.push({ d, term: he, p: prefix(key, en) });
          });
          if (found.length) { slot = index; options.push(...found); }
          return;
        }
        if (!HEBREW.test(token) || token.length < 3) return;
        if (parseQuery(token, this.brands).detected.length) return;
        const plain = stripFinal(token);
        if (known.has(plain) || known.has(token)) return;
        const max = token.length <= 4 ? 1 : 2;
        const found = [];
        known.forEach((term) => {
          const d = editDistance(plain, stripFinal(term), max);
          if (d <= max) found.push({ d, term, p: prefix(plain, term) });
        });
        if (found.length) { slot = index; options.push(...found); }
      });
      if (slot < 0) return [];
      // Closest first; ties: the longer shared prefix (typos are mostly a dropped or swapped
      // letter further in), then the shorter name.
      options.sort((a, b) => a.d - b.d || b.p - a.p || a.term.length - b.term.length);
      const seen = new Set();
      return options.filter((o) => !seen.has(o.term) && seen.add(o.term)).slice(0, 3).map((o) => tokens.map((t, i) => (i === slot ? o.term : t)).join(' '));
    }

    /**
     * Last resort for a word nobody knows: the API's wildcard ("בו*ון", "לוט*") finds titles
     * that only differ in the middle or the end; the closest word in them is the candidate.
     */
    async wildcardCandidates(text, signal) {
      const tokens = text.split(/\s+/);
      const index = tokens.findIndex((t) => HEBREW.test(t) && t.length >= 4 && !STOP_HE.has(t) && !parseQuery(t, this.brands).detected.length);
      if (index < 0) return [];
      const token = tokens[index];
      const patterns = [`${token.slice(0, 2)}*${token.slice(-2)}`, `${token.slice(0, 3)}*`];
      const words = new Set();
      for (const pattern of patterns) {
        try {
          const url = new URL(this.dataset.api);
          Object.entries({ api_key: this.dataset.key, q: pattern, maxResults: 6, items: true, facets: false, suggestions: true, suggestionsMaxResults: 8, categories: false, pages: false, output: 'json', 'restrictBy[quantity]': '1|' }).forEach(([k, v]) => url.searchParams.set(k, v));
          const response = await fetch(url, { signal });
          if (!response.ok) continue;
          const data = await response.json();
          (data.items || []).forEach((item) => String(item.title || '').split(/\s+/).forEach((w) => words.add(w)));
          (data.suggestions || []).forEach((text) => String(text).split(/\s+/).forEach((w) => words.add(w)));
          if ((data.totalItems || 0) > 0 && (data.totalItems || 0) < 200) break; // a tight pattern found the family
        } catch (error) {
          if (error.name === 'AbortError') throw error;
        }
      }
      const plain = stripFinal(token);
      const max = token.length <= 4 ? 1 : 2;
      const found = [];
      words.forEach((w) => {
        if (!HEBREW.test(w) || STOP_HE.has(w) || w.length < 3) return;
        const d = editDistance(plain, stripFinal(w), max);
        if (d <= max) found.push({ d, term: w });
      });
      found.sort((a, b) => a.d - b.d || a.term.length - b.term.length);
      const seen = new Set();
      return found.filter((o) => !seen.has(o.term) && seen.add(o.term)).slice(0, 3).map((o) => tokens.map((t, i) => (i === index ? o.term : t)).join(' '));
    }
    knownTerms() {
      if (this.terms && this.termsSize === this.vocab.length + this.families.size) return this.terms;
      const set = new Set();
      const add = (word) => word && HEBREW.test(word) && !STOP_HE.has(word) && set.add(word);
      [...ROOMS, ...STYLES, ...FEATURES, ...SHAPES].forEach((entry) => entry.words.forEach(add));
      COLOURS.forEach(add);
      this.vocab.forEach(([he]) => add(he));
      this.brands.forEach((he) => add(he));
      this.families.forEach((name) => name.split(' ').forEach(add));
      (this.data.popular || []).forEach((text) => String(text).split(/\s+/).forEach(add));
      this.terms = set;
      this.termsSize = this.vocab.length + this.families.size;
      return set;
    }

    /**
     * The received page in a better order for a text query: exact title words first (the
     * brand the shopper typed), then partial matches, then the API's own order — which
     * already carries its merchandising. Refinement-only queries keep the bestseller order.
     */
    /**
     * A query that names a variant SKU ("31501090-200290") points the card at that variant:
     * its link (?variant=), picture and price, and the card says which size it is. Customer
     * service and shoppers with a catalogue number land on the right size in one tap.
     */
    pinSku(records, text) {
      // The typed query as well: the parser reads "80150" in "31501090-80150" as a size.
      const sources = [text, this.query].filter(Boolean);
      const keys = new Set(sources.flatMap((t) => [skuKey(t), ...String(t).split(/\s+/).map(skuKey)]).filter((k) => k.length >= 8));
      if (!keys.size) return records;
      return records.map((record) => {
        const variant = (record.variants || []).find((v) => keys.has(skuKey(v.sku)));
        if (!variant) return record;
        return {
          ...record,
          link: variant.link,
          image: variant.image || record.image,
          price: variant.price || record.price,
          priceMax: variant.price || record.price,
          was: variant.was > variant.price ? variant.was : 0,
          soldOut: !variant.available,
          skuMatch: { sku: variant.sku, size: variant.size, available: variant.available },
        };
      });
    }

    rerank(records, text) {
      return this.applyRank(this.baseRank(records, text), text);
    }

    baseRank(records, text) {
      records = this.pinSku(records, text);
      if (this.sort === 'price-asc' || this.sort === 'price-desc') {
        // The API sorts by the product's base price; the card shows the cheapest available
        // variant — order the page by that so the shopper sees what was asked for.
        const dir = this.sort === 'price-asc' ? 1 : -1;
        return records.map((r, i) => ({ r, i })).sort((a, b) => dir * (a.r.price - b.r.price) || a.i - b.i).map((x) => x.r);
      }
      if (this.sort) return records; // an explicit sort is the order
      const tokens = (text || '').toLowerCase().split(/\s+/).filter((t) => t && !GENERIC.test(t) && !/\d/.test(t));
      // The shopper's finished rug-finder answers (assets/customer-intent.js) lift the rugs
      // that fit them — pets, easy cleaning, the chosen style… — within the page.
      const signals = new Set(window.CustomerIntent?.completed?.()?.signals || []);
      if (!tokens.length && !signals.size) return records;
      const scored = records.map((record, index) => {
        const title = record.title.toLowerCase();
        const words = title.split(/\s+/).map(stripFinal);
        let score = signals.size ? (record.traits || []).filter((t) => signals.has(t)).length * 1.5 : 0;
        tokens.forEach((token) => {
          const plain = stripFinal(token);
          const he = LATIN.test(token) ? this.brands.get(token) : null;
          if (words.includes(plain) || words.includes(token) || (he && words.includes(he))) score += 3;
          else if (title.includes(token)) score += 1;
        });
        return { record, index, score };
      });
      return scored.sort((a, b) => b.score - a.score || a.index - b.index).map((s) => s.record);
    }

    /* ---------------------------------------------------------------- learned ranking (FLAGS.learnedRank) */

    /** 'learned' | 'holdout' (kept per session, so one shopper sees one order) | 'off'. */
    rankArm() {
      if (!FLAGS.learnedRank || !analyticsOrigin()) return 'off';
      if (!this.rankBucket) {
        let bucket = null;
        try {
          bucket = sessionStorage.getItem('site-search:rank-arm');
          if (bucket !== 'learned' && bucket !== 'holdout') {
            bucket = Math.random() < RANK_HOLDOUT ? 'holdout' : 'learned';
            sessionStorage.setItem('site-search:rank-arm', bucket);
          }
        } catch (error) {
          bucket = 'learned';
        }
        this.rankBucket = bucket;
      }
      return this.rankBucket;
    }

    /** The rank map for one search key: { version, boosts: { productId: -1…1 }, explore: [productId] } or null. */
    async loadRankMap(key, signal) {
      this.rankMaps = this.rankMaps || new Map();
      const cached = this.rankMaps.get(key);
      if (cached && Date.now() - cached.at < RANK_TTL_MS) return cached.map;
      try {
        const url = new URL('/api/analytics/search/rank', analyticsOrigin());
        url.searchParams.set('key', key);
        const response = await fetch(url, { signal: withTimeout(signal, 2500), credentials: 'omit' });
        const data = response.ok ? await response.json() : null;
        const raw = data && (data.data || data);
        const map = raw && raw.boosts && typeof raw.boosts === 'object' ? {
          version: String(raw.version || ''),
          boosts: Object.fromEntries(Object.entries(raw.boosts).filter(([, w]) => Number.isFinite(Number(w))).map(([id, w]) => [String(id), Math.max(-1, Math.min(1, Number(w)))])),
          explore: (Array.isArray(raw.explore) ? raw.explore : []).map(String).slice(0, 3),
        } : null;
        this.rankMaps.set(key, { at: Date.now(), map });
        return map;
      } catch (error) {
        if (error.name !== 'AbortError') this.rankMaps.set(key, { at: Date.now(), map: null }); // none for this key, for a while
        return null;
      }
    }

    /**
     * The learned order on top of the page's order: each product moves at most RANK_MAX_MOVE places
     * by its boost, the SKU pin stays first, and one "explore" product (little data so far) is given
     * a fair place (5th) so the map keeps learning.
     */
    applyRank(records, text) {
      const map = this.rank?.arm === 'learned' ? this.rank.map : null;
      if (this.rank) {
        this.rank.applied = false;
        this.rank.explored = null;
      }
      if (!map || this.sort || records.length < 2) return records;
      const pinned = /\d{4,}/.test(text || '') ? 1 : 0; // a catalogue number: the engine's (pinned) first stays first
      const head = records.slice(0, pinned);
      const moved = records.slice(pinned)
        .map((record, index) => {
          const boost = map.boosts[String(record.id)] || 0;
          return { record, key: index - boost * RANK_MAX_MOVE, boost, index };
        })
        .sort((a, b) => a.key - b.key || b.boost - a.boost || a.index - b.index)
        .map((x) => x.record);
      const explore = map.explore.map((id) => moved.findIndex((r) => String(r.id) === id)).find((i) => i > 4);
      if (explore !== undefined) {
        const [record] = moved.splice(explore, 1);
        moved.splice(4, 0, record);
        this.rank.explored = String(record.id);
      }
      this.rank.applied = true;
      return [...head, ...moved];
    }

    /** What every results / click event carries so the analytics can learn and compare. */
    rankContext() {
      const rank = this.rank || {};
      return { rankKey: rank.key || null, rankArm: rank.arm || 'off', rankVersion: rank.applied ? rank.map?.version || null : null, explored: rank.explored || null };
    }

    /**
     * Suggestion rows: the shopper's own recent searches that continue the typed text, then
     * complete intents built from the reply's facets (each one a refinement that really has
     * products, with its count), then a couple of the API's completions.
     */
    suggestionRows(view, refinements) {
      const { typed, parsed, reply } = view;
      const rows = [];
      const lowered = typed.toLowerCase();
      this.readRecent().filter((text) => text.toLowerCase() !== lowered && text.toLowerCase().startsWith(lowered)).slice(0, 2).forEach((text) => rows.push({ text, suggest: text, source: 'recent', icon: CLOCK_ICON, match: typed }));
      const has = (kind) => refinements.some((r) => r.kind === kind);
      const facet = (attribute) => reply.facets.find((f) => f.attribute === attribute)?.buckets || [];
      // A keyboard-layout correction reads in the catalogue's words ("סידני 80×150", not "סידנ").
      const words = view.corrected?.kind === 'layout' && view.corrected.applied ? view.corrected.to : parsed.text;
      const base = words || [this.labels.rugs, ...refinements.map((r) => this.describe(r))].join(' ');
      const intents = [];
      if (!has('size')) facet(this.attrs.size).filter((b) => b.count > 0 && b.value !== 'OS').slice(0, 2).forEach((b) => intents.push({ label: `${base} ${sizeLabel(b.value)}`, count: b.count, refinement: { kind: 'size', value: b.value, label: sizeLabel(b.value) } }));
      if (!has('shape')) facet(this.attrs.shape).filter((b) => b.count > 0 && b.value !== 'מלבן').slice(0, 1).forEach((b) => intents.push({ label: `${base} ${b.value}`, count: b.count, refinement: { kind: 'shape', value: b.value, label: b.value } }));
      if (!has('color')) facet(this.attrs.color).filter((b) => b.count > 0 && b.value !== 'צבעוני').slice(0, 1).forEach((b) => intents.push({ label: `${base} ${b.title || b.value}`, count: b.count, refinement: { kind: 'color', value: b.value, label: b.title || b.value } }));
      if (!has('room')) {
        const room = this.roomsByUse()[0];
        intents.push({ label: `${base} ל${room.label}`, refinement: { kind: 'room', value: room.tag, label: room.label } });
      }
      intents.slice(0, 4).forEach((intent) => rows.push({ text: intent.label, count: intent.count, refinement: intent.refinement, source: 'intent', icon: SEARCH_ICON, match: base }));
      reply.suggestions.filter((text) => text.trim() && text.trim() !== typed && !rows.some((r) => r.text === text)).slice(0, 2).forEach((text) => rows.push({ text, suggest: text, source: 'suggestion', icon: SEARCH_ICON, match: typed }));
      return rows;
    }

    rememberRoom(tag) {
      const counts = storage.read(KEYS.rooms) || {};
      counts[tag] = (counts[tag] || 0) + 1;
      storage.write(KEYS.rooms, counts);
    }

    /* ---------------------------------------------------------------- device memory */

    readRecent() {
      const list = storage.read(KEYS.recent);
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string').slice(0, RECENT_MAX) : [];
    }

    remember(query) {
      if (!query) return;
      storage.write(KEYS.recent, [query, ...this.readRecent().filter((item) => item !== query)].slice(0, RECENT_MAX));
    }

    readViewed() {
      const list = storage.read(KEYS.viewed);
      return Array.isArray(list) ? list.filter((item) => item && item.link && item.title).slice(0, VIEWED_MAX) : [];
    }

    rememberViewed(record) {
      storage.write(KEYS.viewed, [record, ...this.readViewed().filter((item) => item.id !== record.id)].slice(0, VIEWED_MAX));
    }

    rememberSize(value) {
      const counts = storage.read(KEYS.sizes) || {};
      counts[value] = (counts[value] || 0) + 1;
      storage.write(KEYS.sizes, counts);
    }

    /* ---------------------------------------------------------------- analytics
       One DOM event per step for GTM/anyone (`site-search`, detail = {name, …}), a dataLayer
       push, and the CJA SDK's own vocabulary (it rejects names outside its contract). */

    track(name, props = {}) {
      const detail = { name, query: this.query, ...props, ts: Date.now() };
      try {
        document.dispatchEvent(new CustomEvent('site-search', { detail }));
        (window.dataLayer = window.dataLayer || []).push({ event: 'site_search', ...Object.fromEntries(Object.entries(detail).map(([k, v]) => [`ss_${k}`, v])) });
        const cja = window.CJA;
        if (!cja || typeof cja.track !== 'function') return;
        const base = { query: this.query, source: 'site-search', area: this.dataset.cjaArea || 'search' };
        if (name === 'search_query') cja.track('search', { ...base, text: props.text, refinements: props.refinements, fallback: props.fallback, searchSource: props.source, ruleIds: props.ruleIds });
        else if (name === 'search_results') cja.track('search_results', { ...base, text: props.text, count: props.count, zeroResults: props.zeroResults, refinements: props.refinements, recovered: props.recovered, correctedFrom: props.correctedFrom, correctedTo: props.correctedTo, correctionKind: props.correctionKind, dropped: props.dropped, searchSource: props.source, ruleIds: props.ruleIds, engine: 'searchanise-api', impressions: props.impressions, rankKey: props.rankKey, rankArm: props.rankArm, rankVersion: props.rankVersion, explored: props.explored });
        else if (name === 'search_zero_results') cja.track('search_results', { ...base, text: props.text, count: 0, zeroResults: true, refinements: props.refinements, recovered: props.recovered, correctedTo: props.correctedTo, correctionKind: props.correctionKind, dropped: props.dropped, searchSource: props.source, ruleIds: props.ruleIds, engine: 'searchanise-api' });
        else if (name === 'search_product_click') cja.track('search_result_click', { ...base, productId: props.productId, position: props.position, resultCount: props.resultCount, msFromOpen: props.msFromOpen, resultSource: props.source, correctedFrom: props.correctedFrom, correctedTo: props.correctedTo, correctionKind: props.correctionKind, rankKey: props.rankKey, rankArm: props.rankArm, rankVersion: props.rankVersion });
        else if (name === 'search_filter_click') cja.track('filter_apply', { ...base, kind: props.kind, value: props.value, on: props.on });
        else cja.track('cta_click', { ...base, id: name, ...props });
      } catch (error) {
        // analytics must never break the search
      }
    }
  }

  customElements.define('site-search', SiteSearch);
}
